import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, onValue } from 'firebase/database';
import { app, database } from '../firebaseConfig';
import { useActiveWorld } from '../hooks/useActiveWorld';
import ApiStagingReview from './ApiStagingReview';
import SmartReviewQueue from './SmartReviewQueue';
import ImportRuleEditor from './ImportRuleEditor';
import './ApiSettings.css';

const ApiSettings = () => {
  const { userId, isOwner } = useActiveWorld();
  // Multi-Key Support
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPurpose, setNewKeyPurpose] = useState('import'); // 'import' | 'setup'
  const [newKeyMode, setNewKeyMode] = useState('staging'); // 'staging' | 'auto' | 'smart'
  const [newKeyTemplate, setNewKeyTemplate] = useState('');
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedMcp, setCopiedMcp] = useState(false);
  const [webhookDrafts, setWebhookDrafts] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState('quickstart');
  const [showStagingReview, setShowStagingReview] = useState(false);
  const [pendingImports, setPendingImports] = useState({ batches: 0, contacts: 0 });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedKeyId, setExpandedKeyId] = useState(null);
  const [detailTab, setDetailTab] = useState('overview'); // 'overview' | 'behavior' | 'connect' | 'activity'
  const [footerTab, setFooterTab] = useState(null); // null | 'how' | 'docs'
  const [autoLogs, setAutoLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const [showSmartReview, setShowSmartReview] = useState(false);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const [emailTemplates, setEmailTemplates] = useState([]);
  // Intent-first connection UX: per key, what does the user want to do?
  const [keyPurpose, setKeyPurpose] = useState({}); // { [keyId]: 'import' | 'setup' }
  const [keyAdvanced, setKeyAdvanced] = useState({}); // { [keyId]: bool }

  const functions = getFunctions(undefined, 'europe-west1');

  const DEDUP_FIELD_OPTIONS = [
    { key: 'lead_email', label: 'Email', fixed: true },
    { key: 'business_name', label: 'Business Name' },
    { key: 'lead_telephone', label: 'Telephone' },
    { key: 'lead_website', label: 'Website' },
  ];

  // API-Keys laden
  useEffect(() => {
    loadApiKeys();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const customRef = ref(database, `World/${userId}/settings/customTopics`);
    const unsub = onValue(customRef, (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([id, t]) => ({
          id,
          topic: t.topic || id,
          type: t.type || 'intro'
        }));
        setEmailTemplates(list);
      } else {
        setEmailTemplates([]);
      }
    });
    return () => unsub();
  }, [userId]);

  // Auto-Load logs when expanding an auto/smart mode key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (expandedKeyId) {
      const key = apiKeys.find(k => k.id === expandedKeyId);
      if ((key?.mode === 'auto' || key?.mode === 'smart') && !autoLogs[expandedKeyId]) {
        loadAutoLogs(expandedKeyId);
      }
    }
  }, [expandedKeyId, apiKeys]);

  // Auto-select the first key when the list becomes available so the workbench
  // is never empty on load.
  useEffect(() => {
    if (!showCreateForm && !expandedKeyId && apiKeys.length > 0) {
      setExpandedKeyId(apiKeys[0].id);
    }
  }, [apiKeys, expandedKeyId, showCreateForm]);

  // Reset the detail sub-tab only when the user actually switches to another key.
  useEffect(() => {
    if (expandedKeyId) setDetailTab('overview');
  }, [expandedKeyId]);

  // Derive purpose from the key itself so the detail pane never asks
  // "What do you want to do?" again for a key that has already been configured.
  useEffect(() => {
    if (!expandedKeyId) return;
    const key = apiKeys.find(k => k.id === expandedKeyId);
    if (!key) return;
    setKeyPurpose(prev => {
      if (prev[expandedKeyId]) return prev;
      return { ...prev, [expandedKeyId]: key.setupEnabled ? 'setup' : 'import' };
    });
  }, [expandedKeyId, apiKeys]);

  // Load review queue count
  useEffect(() => {
    if (!userId) return;
    const hasSmartKey = apiKeys.some(k => k.mode === 'smart');
    if (!hasSmartKey) return;

    const loadReviewCount = async () => {
      try {
        const getQueue = httpsCallable(functions, 'getSmartReviewQueue');
        const result = await getQueue({ userId, limit: 1 });
        setReviewQueueCount(result.data.count || 0);
      } catch { /* silent */ }
    };
    loadReviewCount();
  }, [userId, apiKeys, functions]);

  const loadApiKeys = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const getStatus = httpsCallable(functions, 'getWorldDbApiKeyStatus');
      // 🔒 TEAM SUPPORT: send active-workspace userId so backend serves the
      // owner's API keys when caller is a team member.
      const result = await getStatus({ userId });
      
      // Support both new (multi-key) and old (single-key) format
      if (result.data.hasKeys && result.data.keys) {
        // New multi-key format
        setApiKeys(result.data.keys);
      } else if (result.data.hasKey && result.data.apiKey) {
        // Old single-key format - convert to array
        const legacyKey = {
          id: 'legacy-key',
          apiKey: result.data.apiKey,
          name: result.data.name || 'API Import',
          createdAt: result.data.stats?.createdAt,
          lastUsed: result.data.stats?.lastUsed,
          requestCount: result.data.stats?.requestCount || 0,
          totalStaged: result.data.stats?.totalStaged || 0,
          isActive: result.data.stats?.isActive !== false
        };
        setApiKeys([legacyKey]);
      } else {
        setApiKeys([]);
      }
      
      if (result.data.pending) {
        setPendingImports(result.data.pending);
      }
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setNewKeyName('');
    setNewKeyPurpose('import');
    setNewKeyMode('staging');
    setNewKeyTemplate('');
    setError(null);
    setShowCreateForm(true);
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setNewKeyName('');
  };

  const selectKey = (keyId) => {
    setShowCreateForm(false);
    setExpandedKeyId(keyId);
    setKeyPurpose(prev => ({ ...prev, [keyId]: prev[keyId] || undefined }));
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const generate = httpsCallable(functions, 'generateWorldDbApiKey');
      const result = await generate({ userId, name: newKeyName.trim() });

      if (result.data.success) {
        const chosenMode = newKeyPurpose === 'import' ? newKeyMode : 'staging';
        const chosenTemplate = newKeyPurpose === 'import' ? newKeyTemplate : '';

        // Persist the picked configuration in one follow-up call so the
        // key already reflects the wizard choices when it lands in the list.
        try {
          const update = httpsCallable(functions, 'updateWorldDbApiKey');
          await update({
            userId,
            keyId: result.data.keyId,
            mode: chosenMode,
            autoConfig: { dedupeFields: ['lead_email'], duplicateAction: 'merge' },
            setupEnabled: newKeyPurpose === 'setup',
            defaultTemplate: chosenTemplate || undefined,
          });
        } catch (cfgErr) {
          console.warn('Key created but initial config could not be saved:', cfgErr);
        }

        const newKey = {
          id: result.data.keyId,
          apiKey: result.data.apiKey,
          name: result.data.name,
          createdAt: result.data.created,
          lastUsed: null,
          requestCount: 0,
          totalStaged: 0,
          totalImported: 0,
          isActive: true,
          mode: chosenMode,
          setupEnabled: newKeyPurpose === 'setup',
          defaultTemplate: chosenTemplate || null,
          autoConfig: { dedupeFields: ['lead_email'], duplicateAction: 'merge' },
          mappingMode: 'auto',
          fieldMapping: [],
        };
        setApiKeys(prev => [newKey, ...prev]);
        setKeyPurpose(prev => ({ ...prev, [newKey.id]: newKeyPurpose }));
        setExpandedKeyId(newKey.id);
        setShowKey(false);
        setNewKeyName('');
        setShowCreateForm(false);
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      if (err.code === 'resource-exhausted') {
        setError('Maximum of 10 API keys reached');
      } else {
        setError('Failed to create API key');
      }
    } finally {
      setGenerating(false);
    }
  };

  const updateKeyName = async (keyId) => {
    if (!editingName.trim()) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      const result = await update({ userId, keyId, name: editingName.trim() });
      
      if (result.data.success) {
        setApiKeys(prev => prev.map(k => 
          k.id === keyId ? { ...k, name: editingName.trim() } : k
        ));
        setEditingKeyId(null);
        setEditingName('');
      }
    } catch (err) {
      console.error('Error updating API key name:', err);
      setError('Failed to update API key name');
    } finally {
      setSaving(false);
    }
  };

  const toggleKeyActive = async (keyId, isActive) => {
    setSaving(true);
    setError(null);
    
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, isActive });
      
      setApiKeys(prev => prev.map(k => 
        k.id === keyId ? { ...k, isActive } : k
      ));
    } catch (err) {
      console.error('Error toggling API key:', err);
      setError('Failed to update API key');
    } finally {
      setSaving(false);
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This cannot be undone.')) {
      return;
    }
    
    setDeleting(keyId);
    setError(null);
    
    try {
      const deleteKey = httpsCallable(functions, 'deleteWorldDbApiKey');
      await deleteKey({ userId, keyId });
      
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      
      // Wenn gelöschter Key aufgeklappt war, schließen
      if (expandedKeyId === keyId) {
        setExpandedKeyId(null);
      }
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key');
    } finally {
      setDeleting(null);
    }
  };

  const startEditingKey = (key) => {
    setEditingKeyId(key.id);
    setEditingName(key.name);
  };

  const cancelEditingKey = () => {
    setEditingKeyId(null);
    setEditingName('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Trigger a .json file download in the browser (ready-to-import connection config).
  const downloadJson = (filename, obj) => {
    try {
      const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('downloadJson failed:', e);
    }
  };

  // Pick what this key is for. Choosing "setup" also enables the setup scope.
  const choosePurpose = (keyObj, purpose) => {
    setKeyPurpose(prev => ({ ...prev, [keyObj.id]: purpose }));
    setKeyAdvanced(prev => ({ ...prev, [keyObj.id]: false }));
    if (purpose === 'setup' && keyObj.setupEnabled !== true) {
      toggleSetupEnabled(keyObj.id, true);
    }
    if (purpose === 'import' && keyObj.setupEnabled === true) {
      toggleSetupEnabled(keyObj.id, false);
    }
  };

  const updateKeyMode = async (keyId, newMode, currentAutoConfig) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, mode: newMode, autoConfig: currentAutoConfig || { dedupeFields: ['lead_email'], duplicateAction: 'merge' } });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, mode: newMode } : k
      ));
    } catch (err) {
      console.error('Error updating key mode:', err);
      setError('Failed to update import mode');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetupEnabled = async (keyId, newValue) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, setupEnabled: newValue });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, setupEnabled: newValue } : k
      ));
    } catch (err) {
      console.error('Error updating setup scope:', err);
      setError('Failed to update Account Setup API access');
    } finally {
      setSaving(false);
    }
  };

  const saveWebhook = async (keyId, webhookUrl) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      const result = await update({ userId, keyId, webhookUrl });
      setApiKeys(prev => prev.map(k => {
        if (k.id !== keyId) return k;
        const next = { ...k };
        if (Object.prototype.hasOwnProperty.call(result.data, 'webhookUrl')) next.webhookUrl = result.data.webhookUrl;
        if (Object.prototype.hasOwnProperty.call(result.data, 'webhookSecret')) next.webhookSecret = result.data.webhookSecret;
        return next;
      }));
    } catch (err) {
      console.error('Error saving webhook:', err);
      setError(err.message || 'Failed to save webhook URL');
    } finally {
      setSaving(false);
    }
  };

  const rotateWebhookSecret = async (keyId) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      const result = await update({ userId, keyId, rotateWebhookSecret: true });
      setApiKeys(prev => prev.map(k => (
        k.id === keyId ? { ...k, webhookSecret: result.data.webhookSecret || k.webhookSecret } : k
      )));
    } catch (err) {
      console.error('Error rotating webhook secret:', err);
      setError(err.message || 'Failed to rotate webhook secret');
    } finally {
      setSaving(false);
    }
  };

  const toggleOutreachTrigger = async (keyId, newValue) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, allowOutreachTrigger: newValue });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, allowOutreachTrigger: newValue } : k
      ));
    } catch (err) {
      console.error('Error updating outreach trigger scope:', err);
      setError('Failed to update outreach trigger access');
    } finally {
      setSaving(false);
    }
  };

  const updateAutoConfig = async (keyId, newAutoConfig) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, autoConfig: newAutoConfig });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, autoConfig: newAutoConfig } : k
      ));
    } catch (err) {
      console.error('Error updating auto config:', err);
      setError('Failed to update auto config');
    } finally {
      setSaving(false);
    }
  };

  const toggleDedupeField = (keyId, field, currentConfig) => {
    const currentFields = currentConfig?.dedupeFields || ['lead_email'];
    let newFields;
    if (currentFields.includes(field)) {
      newFields = currentFields.filter(f => f !== field);
    } else {
      newFields = [...currentFields, field];
    }
    if (!newFields.includes('lead_email')) newFields.unshift('lead_email');
    updateAutoConfig(keyId, { ...currentConfig, dedupeFields: newFields });
  };

  const setDuplicateAction = (keyId, action, currentConfig) => {
    updateAutoConfig(keyId, { ...currentConfig, duplicateAction: action });
  };

  const updateMappingMode = async (keyId, newMode) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, mappingMode: newMode });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, mappingMode: newMode } : k
      ));
    } catch (err) {
      console.error('Error updating mapping mode:', err);
      setError('Failed to update mapping mode');
    } finally {
      setSaving(false);
    }
  };

  const updateFieldMapping = async (keyId, newMapping) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, fieldMapping: newMapping });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, fieldMapping: newMapping } : k
      ));
    } catch (err) {
      console.error('Error updating field mapping:', err);
      setError('Failed to update field mapping');
    } finally {
      setSaving(false);
    }
  };

  const addFieldMappingRow = (keyId, currentMapping) => {
    const newMapping = [...(currentMapping || []), { source: '', target: '' }];
    setApiKeys(prev => prev.map(k =>
      k.id === keyId ? { ...k, fieldMapping: newMapping } : k
    ));
  };

  const updateFieldMappingRow = (keyId, index, field, value, currentMapping) => {
    const newMapping = [...(currentMapping || [])];
    newMapping[index] = { ...newMapping[index], [field]: value };
    setApiKeys(prev => prev.map(k =>
      k.id === keyId ? { ...k, fieldMapping: newMapping } : k
    ));
  };

  const removeFieldMappingRow = (keyId, index, currentMapping) => {
    const newMapping = (currentMapping || []).filter((_, i) => i !== index);
    updateFieldMapping(keyId, newMapping);
  };

  const saveFieldMapping = (keyId, currentMapping) => {
    const cleaned = (currentMapping || []).filter(m => m.source?.trim() && m.target?.trim());
    updateFieldMapping(keyId, cleaned);
  };

  const loadAutoLogs = async (keyId) => {
    setLoadingLogs(prev => ({ ...prev, [keyId]: true }));
    try {
      const getLogs = httpsCallable(functions, 'getApiAutoLogs');
      const result = await getLogs({ userId, keyId, limit: 20 });
      if (result.data.success) {
        setAutoLogs(prev => ({ ...prev, [keyId]: result.data.logs }));
      }
    } catch (err) {
      console.error('Error loading auto logs:', err);
    } finally {
      setLoadingLogs(prev => ({ ...prev, [keyId]: false }));
    }
  };

  const updateSmartConfig = async (keyId, newSmartConfig) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, smartConfig: newSmartConfig });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, smartConfig: newSmartConfig } : k
      ));
    } catch (err) {
      console.error('Error updating smart config:', err);
      setError('Failed to update smart config');
    } finally {
      setSaving(false);
    }
  };

  const updateDefaultTemplate = async (keyId, templateId) => {
    setSaving(true);
    setError(null);
    try {
      const update = httpsCallable(functions, 'updateWorldDbApiKey');
      await update({ userId, keyId, defaultTemplate: templateId || null });
      setApiKeys(prev => prev.map(k =>
        k.id === keyId ? { ...k, defaultTemplate: templateId || null } : k
      ));
    } catch (err) {
      console.error('Error updating default template:', err);
      setError('Failed to update default template');
    } finally {
      setSaving(false);
    }
  };

  const toggleSmartConfigBool = (keyId, field, currentConfig) => {
    const config = currentConfig || getDefaultSmartConfig();
    updateSmartConfig(keyId, { ...config, [field]: !config[field] });
  };

  const getDefaultSmartConfig = () => ({
    duplicateAction: 'merge',
    stripPlusAddressing: true,
    enableDomainDedup: true,
    reviewThreshold: 0.60,
    enableSignalEvents: true,
    enableScoring: true,
    enableCleaner: false,
    enableSpy: false,
    spyModules: ['core', 'email', 'social'],
    mergePolicies: {},
  });

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 6) + '••••••••••••••••••••••••' + key.substring(key.length - 4);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  // API Base URL - dynamisch basierend auf aktuellem Firebase Projekt (dev/prod)
  const projectId = app.options.projectId;
  const apiBaseUrl = `https://europe-west1-${projectId}.cloudfunctions.net`;

  // Code Examples - WICHTIG: Kein echter API Key in den Beispielen!
  const codeExamples = {
    curl: `curl -X POST "${apiBaseUrl}/worldDbApiImport" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "contacts": [
      {
        "lead_email": "john@example.com",
        "dm_name": "John Doe",
        "dm_role": "CEO",
        "business_name": "Example Corp",
        "lead_telephone": "+1234567890",
        "lead_website": "https://example.com"
      }
    ],
    "import_name": "My Custom List"
  }'

# import_name is optional - defaults to your API key name`,
    
    javascript: `// Using fetch
const response = await fetch(
  "${apiBaseUrl}/worldDbApiImport",
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "YOUR_API_KEY"
    },
    body: JSON.stringify({
      contacts: [
        {
          lead_email: "john@example.com",
          dm_name: "John Doe",
          dm_role: "CEO",
          business_name: "Example Corp"
        }
      ],
      // Optional: override default import name
      import_name: "Facebook Leads"
    })
  }
);

const result = await response.json();
console.log(result);`,

    python: `import requests

url = "${apiBaseUrl}/worldDbApiImport"

headers = {
    "Content-Type": "application/json",
    "x-api-key": "YOUR_API_KEY"
}

data = {
    "contacts": [
        {
            "lead_email": "john@example.com",
            "dm_name": "John Doe",
            "dm_role": "CEO",
            "business_name": "Example Corp"
        }
    ],
    # Optional: custom import name for filtering
    "import_name": "LinkedIn Scraper"
}

response = requests.post(url, json=data, headers=headers)
print(response.json())`,

    zapier: `// In Zapier Webhooks by Zapier (POST)
URL: ${apiBaseUrl}/worldDbApiImport

Headers:
  Content-Type: application/json
  x-api-key: YOUR_API_KEY

Body (JSON):
{
  "lead_email": "{{email}}",
  "dm_name": "{{first_name}} {{last_name}}",
  "dm_role": "{{job_title}}",
  "business_name": "{{company}}",
  "import_name": "Zapier Form Leads"
}

// import_name is optional`
  };

  // Full one-shot Account Setup payload — every field the API can process.
  // A single POST to /worldDbApiSetup runs the entire setup (foundation +
  // AI clone + footer + templates) in the background.
  const setupExampleJson = `{
  "external_ref": "customer-4711",

  "identity": {
    "company": "Nordwind Consulting",
    "website": "https://nordwind.com",
    "aboutUs": "We help B2B SaaS teams book more qualified demos through founder-led cold outreach.",
    "offer": "Done-for-you outbound: list building, copywriting, sending and reply handling.",
    "usps": ["Founder-led, not junior SDRs", "Reply-rate guarantee", "Live in 14 days"],
    "senderName": "Max Mustermann",
    "senderRole": "Founder",
    "senderEmail": "max@nordwind.com",
    "emojis": false,
    "language": "de"
  },

  "audience": {
    "description": "B2B SaaS companies, 10-50 employees, founders & heads of growth.",
    "painPoints": ["Too few qualified demos", "SDRs too expensive"],
    "industry": "SaaS",
    "region": "DACH",
    "decisionMakerRole": "Head of Growth",
    "excludeIndustries": ["Agencies", "Freelancers"],
    "companySize": "SMB"
  },

  "brain": {
    "emails": [
      { "subject": "Quick question", "body": "Hey {{firstName}}, saw you just shipped ..." },
      { "subject": "An idea for {{company}}", "body": "Hi, short one ..." }
    ],
    "subjectLines": ["Quick question", "Idea for {{company}}"],
    "tone": "casual",
    "voiceDescription": "Direct, warm, no marketing fluff.",
    "taboos": ["Dear Sir or Madam", "I hope this email finds you well"],
    "signOff": { "mode": "fixed", "fixed": "Beste Grüße, {{user_name}}" },
    "voiceContextEnabled": true,
    "quizAnswers": { "favoriteOpener": "Pattern-interrupt question" }
  },

  "campaign": {
    "uniqueAdvantage": "Founder-led outreach, no junior SDRs.",
    "painPoint": "Pipeline dries up between referrals.",
    "goal": "Book a 15-min intro call.",
    "references": "Helped Acme go from 2 to 9 demos/week in 6 weeks.",
    "fixedFacts": ["We never use junior SDRs", "Live within 14 days"],
    "angles": ["Time-to-pipeline", "Founder credibility"],
    "taboos": ["Guaranteed revenue numbers"],
    "extraRules": "Never promise specific revenue figures.",
    "proof": {
      "caseStudies": ["Acme: 2 to 9 demos/week in 6 weeks"],
      "testimonials": ["\\"Best outbound partner we've had\\" — CEO, Acme"],
      "notableClients": ["Acme", "Globex"],
      "stats": ["Avg. 8% reply rate", "120+ campaigns shipped"]
    },
    "voiceAdherence": "balanced",
    "briefAdherence": "balanced",
    "language": "de"
  },

  "templates": {
    "introExamples": ["A top-rated intro email you already use ..."],
    "followUpExamples": ["A short follow-up that performed well ..."]
  },

  "options": {
    "forceFullSetup": false,
    "skipLeadResearch": true,
    "skipTemplates": false,
    "activateCloneTemplate": true
  }
}`;

  // Minimal required payload — the smallest valid Call.
  const setupMinimalJson = `{
  "identity": {
    "aboutUs": "We help B2B SaaS teams book more qualified demos through founder-led outreach."
  },
  "audience": {
    "description": "B2B SaaS, 10-50 employees, founders & heads of growth."
  },
  "brain": {
    "emails": [
      { "subject": "Quick question", "body": "Hey, noticed you just shipped ..." },
      { "subject": "An idea", "body": "Hi, short one ..." }
    ]
  }
}`;

  // Lists every contact field the API accepts together with the most common
  // aliases the backend auto-maps (see `mapContactFields` in
  // functions/WorldDbApi.js). Group/category is used in the UI to render
  // the fields in logical sections (Basic / Address / Social / …). The
  // `aliases` array lets users send data from any source system without
  // first renaming columns — they're displayed verbatim in the docs table.
  const availableFields = [
    // ── Basic ──────────────────────────────────────────────────────
    { field: 'lead_email', label: 'Email', group: 'Basic', description: 'Primary contact email (used for deduplication)', aliases: ['email', 'mail', 'e-mail', 'emailAddress', 'contact_email'] },
    { field: 'business_name', label: 'Business Name', group: 'Basic', description: 'Company or business name', aliases: ['company', 'companyName', 'business', 'firm', 'organization', 'name'] },
    { field: 'lead_telephone', label: 'Phone', group: 'Basic', description: 'Phone number', aliases: ['phone', 'telefon', 'tel', 'telephone', 'phoneNumber', 'mobile'] },
    { field: 'lead_website', label: 'Website', group: 'Basic', description: 'Website URL', aliases: ['website', 'url', 'web', 'homepage', 'site'] },
    { field: 'lead_description', label: 'Description', group: 'Basic', description: 'Notes / context that helps the AI personalize emails', aliases: ['description', 'notes', 'note', 'comment', 'beschreibung'] },
    { field: 'lead_category', label: 'Category', group: 'Basic', description: 'Industry / vertical / segment', aliases: ['category', 'type', 'branche', 'kategorie'] },
    { field: 'lead_title', label: 'Title / Role', group: 'Basic', description: 'Job title or position (stored on the lead itself)', aliases: ['title', 'position', 'job_title', 'jobTitle', 'role', 'rolle'] },

    // ── Address ────────────────────────────────────────────────────
    { field: 'lead_address', label: 'Street Address', group: 'Address', description: 'Street and house number', aliases: ['address', 'street', 'adresse', 'strasse'] },
    { field: 'lead_postcode', label: 'ZIP / Postcode', group: 'Address', description: 'Postal code', aliases: ['zip', 'postal_code', 'postalCode', 'plz', 'zipcode', 'postcode'] },
    { field: 'lead_city', label: 'City', group: 'Address', description: 'City / town', aliases: ['city', 'stadt', 'ort', 'location'] },
    { field: 'lead_country', label: 'Country', group: 'Address', description: 'Country', aliases: ['country', 'land'] },

    // ── Decision maker ─────────────────────────────────────────────
    { field: 'dm_name', label: 'Decision Maker Name', group: 'Decision Maker', description: 'Full name of the primary contact person', aliases: ['decision_maker_name', 'contact_name', 'contact_person', 'ansprechpartner'] },
    { field: 'dm_role', label: 'Decision Maker Position', group: 'Decision Maker', description: 'Role or position (CEO, Founder, …)', aliases: ['decision_maker_role', 'decision_maker_position', 'contact_role', 'contact_position'] },
    { field: 'dm_email', label: 'Decision Maker Email', group: 'Decision Maker', description: 'Personal email of the decision maker', aliases: ['decision_maker_email', 'contact_person_email'] },
    { field: 'dm_phone', label: 'Decision Maker Phone', group: 'Decision Maker', description: 'Direct phone of the decision maker', aliases: ['decision_maker_phone', 'contact_person_phone'] },
    { field: 'lead_firstname', label: 'First Name', group: 'Decision Maker', description: 'First name — combined with last name into the decision maker', aliases: ['firstName', 'firstname', 'first_name', 'vorname', 'given_name'] },
    { field: 'lead_lastname', label: 'Last Name', group: 'Decision Maker', description: 'Last name — combined with first name into the decision maker', aliases: ['lastName', 'lastname', 'last_name', 'nachname', 'family_name', 'surname'] },

    // ── Social media ───────────────────────────────────────────────
    { field: 'lead_instagram', label: 'Instagram', group: 'Social Media', description: 'Instagram handle or profile URL', aliases: ['instagram', 'ig', 'insta'] },
    { field: 'lead_linkedin', label: 'LinkedIn', group: 'Social Media', description: 'LinkedIn profile URL', aliases: ['linkedin'] },
    { field: 'lead_facebook', label: 'Facebook', group: 'Social Media', description: 'Facebook profile / page URL', aliases: ['facebook', 'fb'] },
    { field: 'lead_tiktok', label: 'TikTok', group: 'Social Media', description: 'TikTok handle or URL', aliases: ['tiktok', 'tik_tok'] },

    // ── Metrics ────────────────────────────────────────────────────
    { field: 'lead_rating', label: 'Rating', group: 'Metrics', description: 'Numeric rating (e.g. Google reviews score)', aliases: ['rating', 'total_rating', 'bewertung'] },
    { field: 'lead_reviews', label: 'Review Count', group: 'Metrics', description: 'Number of reviews', aliases: ['reviews', 'total_reviews', 'review_count'] },
    { field: 'lead_stars', label: 'Stars', group: 'Metrics', description: 'Star rating (0–5)', aliases: ['stars', 'sterne'] },
    { field: 'lead_instagram_followers', label: 'IG Followers', group: 'Metrics', description: 'Instagram follower count (numeric)', aliases: ['instagram_followers', 'followers'] },
    { field: 'lead_instagram_posts', label: 'IG Posts', group: 'Metrics', description: 'Instagram post count (numeric)', aliases: ['instagram_posts', 'ig_posts'] },

    // ── Instagram extras ───────────────────────────────────────────
    { field: 'lead_instagram_username', label: 'IG Username', group: 'Instagram', description: 'Instagram username only (without URL)', aliases: ['instagram_username', 'ig_username'] },
    { field: 'lead_instagram_id', label: 'IG ID', group: 'Instagram', description: 'Internal Instagram user ID', aliases: ['instagram_id', 'ig_id'] },
    { field: 'lead_instagram_category', label: 'IG Category', group: 'Instagram', description: 'Instagram business category', aliases: ['instagram_category', 'ig_category'] },

    // ── Other ──────────────────────────────────────────────────────
    { field: 'lead_link', label: 'Source Link', group: 'Other', description: 'Where the contact was found (URL)', aliases: ['link', 'source_url', 'source_link'] },
    { field: 'lead_image', label: 'Image / Logo', group: 'Other', description: 'Image or logo URL', aliases: ['image', 'photo', 'logo', 'bild'] },
    { field: 'lead_mapsurl', label: 'Google Maps URL', group: 'Other', description: 'Google Maps link', aliases: ['maps_url', 'mapsurl', 'google_maps', 'maps'] },
  ];

  // Group the flat list into sections so the docs table can render them with
  // a sticky group header for each section instead of one giant flat table.
  const groupedFields = availableFields.reduce((acc, field) => {
    const groupName = field.group || 'Other';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(field);
    return acc;
  }, {});
  const FIELD_GROUP_ORDER = ['Basic', 'Address', 'Decision Maker', 'Social Media', 'Metrics', 'Instagram', 'Other'];

  if (loading) {
    return (
      <div className="ap">
        <div className="ap-loading">
          <div className="ap-spinner" />
          <p>Loading API settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ap">
      <header className="ap-page-head">
        <div>
          <h3 className="ap-page-title">API</h3>
          <p className="ap-page-sub">
            Create a key, plug it into another app, and import contacts or set up an account.
          </p>
        </div>
        <span className="ap-badge">{apiKeys.length}/10 keys</span>
      </header>

      {error && (
        <div className="ap-error">
          <i className="fa fa-circle-exclamation" />
          {error}
        </div>
      )}

      {pendingImports.batches > 0 && (
        <div className="ap-pending">
          <span className="ap-icon" aria-hidden>
            <i className="fa fa-inbox" />
          </span>
          <div className="ap-pending-text">
            <strong>{formatNumber(pendingImports.contacts)} contacts</strong>
            <span>waiting to be imported · {pendingImports.batches} batch{pendingImports.batches > 1 ? 'es' : ''} received via API</span>
          </div>
          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={() => setShowStagingReview(true)}
          >
            Review & Import
          </button>
        </div>
      )}

      <div className="ap-workspace">
        <aside className="ap-rail">
          <div className="ap-rail-head">
            <span className="ap-rail-count">{apiKeys.length} / 10</span>
            {apiKeys.length < 10 && (
              <button
                type="button"
                className="altBtn altBtn--save ap-rail-new"
                onClick={openCreate}
                disabled={generating}
              >
                <i className="fa fa-plus" /> New key
              </button>
            )}
          </div>

          {apiKeys.length === 0 ? (
            <div className="ap-rail-empty">
              <p>No keys yet.</p>
              <p>Create one to connect external tools.</p>
            </div>
          ) : (
            <ul className="ap-rail-list">
              {apiKeys.map((key) => {
                const isActive = expandedKeyId === key.id && !showCreateForm;
                const modeLabel = key.mode === 'smart' ? 'Smart' : key.mode === 'auto' ? 'Direct' : 'Review';
                const usage = (key.mode === 'auto' || key.mode === 'smart') ? (key.totalImported || 0) : key.totalStaged;
                return (
                  <li key={key.id}>
                    <button
                      type="button"
                      className={`ap-rail-item ${isActive ? 'active' : ''} ${!key.isActive ? 'inactive' : ''}`}
                      onClick={() => selectKey(key.id)}
                    >
                      <span className="ap-rail-item-body">
                        <span className="ap-rail-item-name">{key.name}</span>
                        <span className="ap-rail-item-meta">
                          <span className={`ap-mode-chip mode-${key.mode || 'staging'}`}>{modeLabel}</span>
                          {!key.isActive && <span className="ap-mode-chip off">Off</span>}
                        </span>
                        <span className="ap-rail-item-stats">
                          {formatNumber(usage)} contacts · {formatNumber(key.requestCount)} calls
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <main className="ap-pane">
          {showCreateForm ? (
            <div className="ap-wizard">
              <header className="ap-wizard-head">
                <span className="ap-section-eyebrow">New key</span>
                <h4 className="ap-section-title">Create an API key</h4>
                <p className="ap-section-sub">
                  Give it a name and decide upfront what this key is allowed to do.
                  You can change any of this later.
                </p>
              </header>

              <div className="ap-field">
                <label className="ap-field-label">Name</label>
                <input
                  type="text"
                  className="ap-input"
                  placeholder="e.g. Zapier, Chrome Extension, Daily import…"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="ap-field">
                <label className="ap-field-label">Purpose</label>
                <div className="ap-choice-grid two">
                  <button
                    type="button"
                    className={`ap-choice ${newKeyPurpose === 'import' ? 'active' : ''}`}
                    onClick={() => setNewKeyPurpose('import')}
                  >
                    <span className="ap-choice-icon"><i className="fa fa-inbox" /></span>
                    <span className="ap-choice-body">
                      <span className="ap-choice-title">Import contacts</span>
                      <span className="ap-choice-desc">Push leads from another app into this database.</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`ap-choice ${newKeyPurpose === 'setup' ? 'active' : ''}`}
                    onClick={() => setNewKeyPurpose('setup')}
                  >
                    <span className="ap-choice-icon"><i className="fa fa-bolt" /></span>
                    <span className="ap-choice-body">
                      <span className="ap-choice-title">Set up an account</span>
                      <span className="ap-choice-desc">Let another app configure identity, voice clone and templates.</span>
                    </span>
                  </button>
                </div>
              </div>

              {newKeyPurpose === 'import' && (
                <>
                  <div className="ap-field">
                    <label className="ap-field-label">Import mode</label>
                    <p className="ap-field-hint">
                      How incoming contacts arrive in your database.
                    </p>
                    <div className="ap-choice-grid three">
                      <button
                        type="button"
                        className={`ap-choice ${newKeyMode === 'staging' ? 'active' : ''}`}
                        onClick={() => setNewKeyMode('staging')}
                      >
                        <span className="ap-choice-icon"><i className="fa fa-clipboard-check" /></span>
                        <span className="ap-choice-body">
                          <span className="ap-choice-title">Review</span>
                          <span className="ap-choice-desc">Land in staging first. You confirm every batch.</span>
                          <span className="ap-choice-tag">Safest</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`ap-choice ${newKeyMode === 'smart' ? 'active' : ''}`}
                        onClick={() => setNewKeyMode('smart')}
                      >
                        <span className="ap-choice-icon"><i className="fa fa-wand-magic-sparkles" /></span>
                        <span className="ap-choice-body">
                          <span className="ap-choice-title">Smart</span>
                          <span className="ap-choice-desc">Validate + dedupe + score. Only unclear rows wait for review.</span>
                          <span className="ap-choice-tag">Recommended</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`ap-choice ${newKeyMode === 'auto' ? 'active' : ''}`}
                        onClick={() => setNewKeyMode('auto')}
                      >
                        <span className="ap-choice-icon"><i className="fa fa-bolt" /></span>
                        <span className="ap-choice-body">
                          <span className="ap-choice-title">Direct</span>
                          <span className="ap-choice-desc">Skip staging entirely. Good for a trusted daily job.</span>
                          <span className="ap-choice-tag">Fastest</span>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="ap-field">
                    <label className="ap-field-label">Default template</label>
                    <p className="ap-field-hint">
                      Optional. Every contact this key delivers is auto-assigned this template.
                    </p>
                    <select
                      className="ap-input"
                      value={newKeyTemplate}
                      onChange={(e) => setNewKeyTemplate(e.target.value)}
                    >
                      <option value="">No template</option>
                      {emailTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.topic}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="ap-wizard-actions">
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={cancelCreate}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="altBtn altBtn--save"
                  onClick={createApiKey}
                  disabled={generating || !newKeyName.trim()}
                >
                  {generating ? 'Creating…' : 'Create key'}
                </button>
              </div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="ap-empty">
              <span className="ap-empty-icon"><i className="fa fa-plug" /></span>
              <h4>Create your first API key</h4>
              <p>Connect external tools and start pushing contacts into your database.</p>
              <button type="button" className="altBtn altBtn--save" onClick={openCreate}>
                <i className="fa fa-plus" /> New key
              </button>
            </div>
          ) : !expandedKeyId ? (
            <div className="ap-empty">
              <span className="ap-empty-icon"><i className="fa fa-arrow-left-long" /></span>
              <h4>Select a key</h4>
              <p>Pick a key from the list on the left to configure it.</p>
            </div>
          ) : null}

          {!showCreateForm && apiKeys.filter(k => k.id === expandedKeyId).map((key) => (
              <div key={key.id} className="ap-detail">
                <header className="ap-detail-head">
                  {editingKeyId === key.id ? (
                    <div className="api-key-item-edit">
                      <input
                        type="text"
                        className="ap-input"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="altBtn altBtn--save"
                        onClick={() => updateKeyName(key.id)}
                        disabled={saving}
                      >
                        {saving ? '…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="altBtn altBtn--petrol"
                        onClick={cancelEditingKey}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="ap-detail-title">
                        <h4>{key.name}</h4>
                        <div className="ap-detail-badges">
                          <span className={`api-key-status-badge ${key.isActive ? 'active' : 'inactive'}`}>
                            {key.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`api-key-mode-badge mode-${key.mode || 'staging'}`}>
                            {key.mode === 'smart' ? 'Smart' : key.mode === 'auto' ? 'Direct' : 'Review'}
                          </span>
                          <span className="ap-detail-stats">
                            {formatNumber((key.mode === 'auto' || key.mode === 'smart') ? (key.totalImported || 0) : key.totalStaged)} contacts · {formatNumber(key.requestCount)} calls
                          </span>
                        </div>
                      </div>
                      <div className="ap-detail-actions">
                        <button
                          type="button"
                          className="api-key-item-action"
                          onClick={() => startEditingKey(key)}
                          title="Rename"
                        >
                          <i className="fa fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="api-key-item-action"
                          onClick={() => toggleKeyActive(key.id, !key.isActive)}
                          title={key.isActive ? 'Deactivate' : 'Activate'}
                          disabled={saving}
                        >
                          <i className={`fa ${key.isActive ? 'fa-power-off' : 'fa-play'}`} />
                        </button>
                        <button
                          type="button"
                          className="api-key-item-action delete"
                          onClick={() => deleteApiKey(key.id)}
                          title="Delete"
                          disabled={deleting === key.id}
                        >
                          {deleting === key.id ? <span className="api-btn-spinner small" /> : <i className="fa fa-trash" />}
                        </button>
                      </div>
                    </>
                  )}
                </header>

                <div className="api-key-accordion-content">
                    {/* Status summary — one sentence, then the four answers */}
                    {(() => {
                      const purpose = keyPurpose[key.id] || (key.setupEnabled ? 'setup' : 'import');
                      const isImport = purpose === 'import';
                      const mode = key.mode === 'smart' ? 'smart' : key.mode === 'auto' ? 'auto' : 'staging';
                      const lead = !key.isActive
                        ? 'This key is off. Incoming requests are rejected until you turn it back on.'
                        : !isImport
                        ? 'This key lets another app set up this account — identity, voice clone and templates.'
                        : mode === 'smart'
                        ? 'This key imports contacts. Clean ones go in automatically; unclear ones wait for you.'
                        : mode === 'auto'
                        ? 'This key imports contacts straight into the database. Nothing waits for review.'
                        : 'This key imports contacts. They wait in a holding area until you confirm them.';
                      const nextStep = mode === 'smart'
                        ? { title: 'AI sorts them', desc: 'Only unclear rows wait for your review.' }
                        : mode === 'auto'
                        ? { title: 'They go in immediately', desc: 'No holding area, no confirmation step.' }
                        : { title: 'You review them first', desc: 'Nothing enters the database until you confirm.' };
                      const templateName = isImport && key.defaultTemplate
                        ? (emailTemplates.find(t => t.id === key.defaultTemplate)?.topic || 'Assigned')
                        : null;
                      const usage = (mode === 'auto' || mode === 'smart') ? (key.totalImported || 0) : key.totalStaged;
                      const usageLabel = (mode === 'auto' || mode === 'smart') ? 'imported' : 'waiting';
                      return (
                        <div className={`ap-status ${!key.isActive ? 'is-off' : ''}`}>
                          <div className="ap-status-lead">
                            <span className={`ap-status-dot ${key.isActive ? 'on' : 'off'}`} aria-hidden />
                            <span className="ap-status-label">{lead}</span>
                          </div>
                          <div className="ap-status-grid">
                            <button type="button" className="ap-status-cell" onClick={() => setDetailTab('behavior')}>
                              <span className="ap-status-cell-label">What it does</span>
                              <span className="ap-status-cell-value">{isImport ? 'Imports contacts' : 'Sets up the account'}</span>
                              <span className="ap-status-cell-desc">
                                {isImport ? 'From Zapier, Make, n8n or a script.' : 'Identity, voice clone, templates.'}
                              </span>
                            </button>
                            {isImport && (
                              <button type="button" className="ap-status-cell" onClick={() => setDetailTab('behavior')}>
                                <span className="ap-status-cell-label">When data arrives</span>
                                <span className="ap-status-cell-value">{nextStep.title}</span>
                                <span className="ap-status-cell-desc">{nextStep.desc}</span>
                              </button>
                            )}
                            {isImport && (
                              <button type="button" className="ap-status-cell" onClick={() => setDetailTab('behavior')}>
                                <span className="ap-status-cell-label">Email template</span>
                                <span className="ap-status-cell-value">{templateName || 'None yet'}</span>
                                <span className="ap-status-cell-desc">
                                  {templateName ? 'Attached to every imported contact.' : 'Change this under Settings.'}
                                </span>
                              </button>
                            )}
                            <button type="button" className="ap-status-cell" onClick={() => setDetailTab('activity')}>
                              <span className="ap-status-cell-label">Used so far</span>
                              <span className="ap-status-cell-value">{formatNumber(usage)} {usageLabel}</span>
                              <span className="ap-status-cell-desc">
                                {key.lastUsed ? `Last request ${formatDate(key.lastUsed)}` : 'No requests yet.'}
                              </span>
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <nav className="ap-detail-tabs" role="tablist">
                      {[
                        { id: 'overview', label: 'The key' },
                        { id: 'behavior', label: 'Settings' },
                        { id: 'connect', label: 'Connect' },
                        { id: 'activity', label: 'History' },
                      ].map(t => (
                        <button
                          key={t.id}
                          type="button"
                          role="tab"
                          aria-selected={detailTab === t.id}
                          className={`ap-detail-tab ${detailTab === t.id ? 'active' : ''}`}
                          onClick={() => setDetailTab(t.id)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </nav>

                    {detailTab === 'overview' && (
                    <>
                    {/* Hero Key Display */}
                    <div className="api-key-hero">
                      <div className="api-key-hero-title">
                        <h4>Secret key</h4>
                        <span className="api-key-hero-subtitle">For Zapier, Make, n8n or a script.</span>
                      </div>
                      <div className="api-key-hero-code">
                        <code>{showKey ? key.apiKey : maskApiKey(key.apiKey)}</code>
                      </div>
                      <div className="api-key-hero-actions">
                        <button
                          type="button"
                          className="altBtn altBtn--petrol"
                          onClick={() => setShowKey(!showKey)}
                        >
                          <i className={`fa ${showKey ? 'fa-eye-slash' : 'fa-eye'}`} />
                          {showKey ? 'Hide' : 'Reveal'}
                        </button>
                        <button
                          type="button"
                          className="altBtn altBtn--save"
                          onClick={() => copyToClipboard(key.apiKey)}
                        >
                          <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} />
                          {copied ? 'Copied' : 'Copy key'}
                        </button>
                      </div>
                    </div>
                    </>
                    )}

                    {detailTab === 'connect' && keyPurpose[key.id] && (
                      <div className="api-link-active">
                        <div className="api-link-active-head">
                          <span className={`api-link-active-badge ${keyPurpose[key.id]}`}>
                            {keyPurpose[key.id] === 'import' ? '📥 Importing contacts' : '⚡ Account setup'}
                          </span>
                          <button
                            className="api-link-switch"
                            onClick={() => setKeyPurpose(prev => ({ ...prev, [key.id]: null }))}
                          >
                            Change
                          </button>
                        </div>

                        {/* The "link cable" — connection card */}
                        <div className="api-link-connect">
                          <p className="api-link-connect-lead">
                            {keyPurpose[key.id] === 'import'
                              ? 'Connect another app (Make, Zapier, n8n, a script…) — it will push leads straight into this account.'
                              : 'Connect another app — it configures this account in one call: AI voice clone, footer, intro & follow-up templates.'}
                          </p>

                          <div className="api-link-field">
                            <label>Connection URL</label>
                            <div className="api-link-field-value">
                              <code>{apiBaseUrl}/{keyPurpose[key.id] === 'import' ? 'worldDbApiImport' : 'worldDbApiSetup'}</code>
                              <button onClick={() => copyToClipboard(`${apiBaseUrl}/${keyPurpose[key.id] === 'import' ? 'worldDbApiImport' : 'worldDbApiSetup'}`)}>
                                {copied ? '✓' : 'Copy'}
                              </button>
                            </div>
                          </div>

                          <div className="api-link-field">
                            <label>Your key — the link cable</label>
                            <div className="api-link-field-value">
                              <code>{showKey ? key.apiKey : maskApiKey(key.apiKey)}</code>
                              <button onClick={() => copyToClipboard(key.apiKey)}>
                                {copied ? '✓' : 'Copy'}
                              </button>
                            </div>
                          </div>

                          <button
                            className="api-link-download"
                            onClick={() => downloadJson(
                              keyPurpose[key.id] === 'import' ? 'bonusclub-import-connection.json' : 'bonusclub-setup-connection.json',
                              keyPurpose[key.id] === 'import'
                                ? {
                                    name: `BonusClub — Import contacts (${key.name})`,
                                    url: `${apiBaseUrl}/worldDbApiImport`,
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'x-api-key': key.apiKey },
                                    bodyExample: { contacts: [{ lead_email: 'john@example.com', dm_name: 'John Doe', business_name: 'Example Corp' }] },
                                  }
                                : {
                                    name: `BonusClub — Account setup (${key.name})`,
                                    url: `${apiBaseUrl}/worldDbApiSetup`,
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'x-api-key': key.apiKey },
                                    bodyExample: {
                                      identity: { aboutUs: 'We help B2B SaaS teams book more demos with founder-led cold outreach.' },
                                      audience: { description: 'B2B SaaS, 10-50 employees, founders & heads of growth.' },
                                      brain: { emails: [{ subject: 'Quick question', body: 'Hey, saw you just shipped ...' }, { subject: 'An idea', body: 'Hi, short one ...' }] },
                                    },
                                    statusUrl: `${apiBaseUrl}/worldDbApiSetupStatus?runId=RUN_ID`,
                                  }
                            )}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M12 3V15M12 15L8 11M12 15L16 11M5 21H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Download connection file (.json)
                          </button>
                          <p className="api-link-download-hint">Import this file into Make / n8n, or hand it to your developer — it contains the URL, your key and an example body. That's all that's needed to connect.</p>
                        </div>

                        {/* Default Template — always editable here so it can be changed at any time */}
                        {keyPurpose[key.id] === 'import' && (
                          <div className="api-key-template-section">
                            <h5>Default Template</h5>
                            <p className="api-key-mode-section-hint">All contacts imported via this key will automatically be assigned this template. You can change or remove it anytime.</p>
                            <div className="api-key-template-select-wrapper">
                              <select
                                className={`api-key-template-select ${key.defaultTemplate ? 'has-template' : ''}`}
                                value={key.defaultTemplate || ''}
                                onChange={(e) => updateDefaultTemplate(key.id, e.target.value)}
                                disabled={saving}
                              >
                                <option value="">No template (default)</option>
                                {emailTemplates.map(t => (
                                  <option key={t.id} value={t.id}>{t.topic}</option>
                                ))}
                              </select>
                              {key.defaultTemplate && (
                                <button
                                  className="api-key-template-clear-btn"
                                  onClick={() => updateDefaultTemplate(key.id, '')}
                                  disabled={saving}
                                  title="Reset to default"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                            {emailTemplates.length === 0 && (
                              <p className="api-key-template-empty-hint">No templates found. Create templates in your email settings first.</p>
                            )}
                          </div>
                        )}

                      </div>
                    )}

                    {/* Account Setup API — deep instructions for setup keys */}
                    {detailTab === 'connect' && keyPurpose[key.id] === 'setup' && (
                    <div className="api-key-setup-api-section">
                      {true && (
                        <div className="api-setup-docs">

                          {/* ───── FOOLPROOF QUICK START ───── */}
                          <div className="api-setup-quickstart">
                            <div className="api-setup-quickstart-head">
                              <span className="api-setup-quickstart-emoji">🚀</span>
                              <div>
                                <h6>Start here — 3 steps</h6>
                                <p>No coding needed. Copy each box, paste it into a terminal, hit enter.</p>
                              </div>
                            </div>

                            {/* Step 1 */}
                            <div className="api-setup-step">
                              <div className="api-setup-step-num">1</div>
                              <div className="api-setup-step-body">
                                <strong>Send your account info</strong>
                                <p>Replace the example text with your real info. You need at least: about-us, target audience, and 2 example emails. Then run it:</p>
                                <div className="api-setup-cmd">
                                  <pre>{`curl -X POST "${apiBaseUrl}/worldDbApiSetup" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${showKey ? key.apiKey : 'YOUR_API_KEY'}" \\
  -d '{
    "identity": { "aboutUs": "We help B2B SaaS teams book more demos with founder-led cold outreach." },
    "audience": { "description": "B2B SaaS, 10-50 employees, founders & heads of growth." },
    "brain": {
      "emails": [
        { "subject": "Quick question", "body": "Hey, saw you just shipped ..." },
        { "subject": "An idea", "body": "Hi, short one ..." }
      ]
    }
  }'`}</pre>
                                  <button
                                    className="api-key-setup-config-copy"
                                    onClick={() => copyToClipboard(`curl -X POST "${apiBaseUrl}/worldDbApiSetup" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${key.apiKey}" \\
  -d '{
    "identity": { "aboutUs": "We help B2B SaaS teams book more demos with founder-led cold outreach." },
    "audience": { "description": "B2B SaaS, 10-50 employees, founders & heads of growth." },
    "brain": {
      "emails": [
        { "subject": "Quick question", "body": "Hey, saw you just shipped ..." },
        { "subject": "An idea", "body": "Hi, short one ..." }
      ]
    }
  }'`)}
                                  >
                                    {copied ? '✓ Copied' : 'Copy'}
                                  </button>
                                </div>
                                <p className="api-setup-step-tip">You instantly get back something like <code>{`{ "runId": "apirun_123" }`}</code>. Copy that <code>runId</code>.</p>
                              </div>
                            </div>

                            {/* Step 2 */}
                            <div className="api-setup-step">
                              <div className="api-setup-step-num">2</div>
                              <div className="api-setup-step-body">
                                <strong>Check when it's done</strong>
                                <p>Paste your <code>runId</code> where it says <code>PASTE_RUNID</code> and run this every few seconds until you see <code>"status": "done"</code>:</p>
                                <div className="api-setup-cmd">
                                  <pre>{`curl "${apiBaseUrl}/worldDbApiSetupStatus?runId=PASTE_RUNID" \\
  -H "x-api-key: ${showKey ? key.apiKey : 'YOUR_API_KEY'}"`}</pre>
                                  <button
                                    className="api-key-setup-config-copy"
                                    onClick={() => copyToClipboard(`curl "${apiBaseUrl}/worldDbApiSetupStatus?runId=PASTE_RUNID" \\
  -H "x-api-key: ${key.apiKey}"`)}
                                  >
                                    {copied ? '✓ Copied' : 'Copy'}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Step 3 */}
                            <div className="api-setup-step">
                              <div className="api-setup-step-num">3</div>
                              <div className="api-setup-step-body">
                                <strong>Done — check your account</strong>
                                <p>That's it. The setup ran automatically. You'll now find in this account:</p>
                                <ul className="api-setup-outcome">
                                  <li>✅ Foundation: about-us, audience &amp; AI guidelines filled in</li>
                                  <li>✅ A trained AI voice clone (from your example emails)</li>
                                  <li>✅ A footer / signature</li>
                                  <li>✅ 2 intro + 2 follow-up email templates</li>
                                </ul>
                                <p className="api-setup-step-tip">Want better results? Add more fields (company, USPs, campaign goal, proof, more emails) — see the full reference below. The more you send, the stronger the setup.</p>
                              </div>
                            </div>
                          </div>

                          <div className="api-setup-reference-divider">
                            <span>Full reference — every field &amp; option</span>
                          </div>

                          {/* Auth */}
                          <div className="api-setup-doc-block">
                            <h6>1. Authentication</h6>
                            <p>Send your API key on every request as a header. The key is bound to <strong>this</strong> account, so the setup always lands here.</p>
                            <div className="api-key-setup-value">
                              <code>x-api-key: {key.apiKey ? `${String(key.apiKey).slice(0, 10)}…` : 'YOUR_API_KEY'}</code>
                              <button onClick={() => copyToClipboard(key.apiKey || 'YOUR_API_KEY')}>
                                {copied ? '✓' : 'Copy key'}
                              </button>
                            </div>
                          </div>

                          {/* Endpoints */}
                          <div className="api-setup-doc-block">
                            <h6>2. Endpoints</h6>
                            <div className="api-key-setup-item full">
                              <label>Run the full setup (POST · JSON body)</label>
                              <div className="api-key-setup-value">
                                <code>{apiBaseUrl}/worldDbApiSetup</code>
                                <button onClick={() => copyToClipboard(`${apiBaseUrl}/worldDbApiSetup`)}>
                                  {copied ? '✓' : 'Copy'}
                                </button>
                              </div>
                            </div>
                            <div className="api-key-setup-item full">
                              <label>Poll the run until done (GET · ?runId=…)</label>
                              <div className="api-key-setup-value">
                                <code>{apiBaseUrl}/worldDbApiSetupStatus</code>
                                <button onClick={() => copyToClipboard(`${apiBaseUrl}/worldDbApiSetupStatus`)}>
                                  {copied ? '✓' : 'Copy'}
                                </button>
                              </div>
                            </div>
                            <div className="api-key-setup-item full">
                              <label>Optional — rebuild only footer + templates (POST)</label>
                              <div className="api-key-setup-value">
                                <code>{apiBaseUrl}/worldDbApiSetupTemplates</code>
                                <button onClick={() => copyToClipboard(`${apiBaseUrl}/worldDbApiSetupTemplates`)}>
                                  {copied ? '✓' : 'Copy'}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Flow */}
                          <div className="api-setup-doc-block">
                            <h6>3. Flow</h6>
                            <ol className="api-setup-flow">
                              <li><strong>POST</strong> <code>/worldDbApiSetup</code> with the JSON below → you instantly get back <code>{`{ runId }`}</code>.</li>
                              <li>The pipeline runs in the background: <em>foundation → AI clone → footer → templates</em>.</li>
                              <li><strong>GET</strong> <code>/worldDbApiSetupStatus?runId=…</code> every few seconds until <code>status: "done"</code>.</li>
                            </ol>
                          </div>

                          {/* Required */}
                          <div className="api-setup-doc-block">
                            <h6>4. Required fields</h6>
                            <ul className="api-setup-fieldlist">
                              <li><code>identity.aboutUs</code> — business description (min 30 chars).</li>
                              <li><code>audience.description</code> — who you target.</li>
                              <li><code>brain.emails</code> — at least 2 sample emails (each with a <code>body</code>) to train the clone.</li>
                            </ul>
                            <p className="api-setup-note">Everything else is optional — empty/missing fields are tolerated and sensible defaults apply.</p>
                          </div>

                          {/* Field reference */}
                          <div className="api-setup-doc-block">
                            <h6>5. What every field does</h6>

                            <div className="api-setup-group">
                              <span className="api-setup-group-title">identity — who the account is</span>
                              <ul className="api-setup-fieldlist">
                                <li><code>aboutUs</code> ✅ — core fuel: vault "About us", guidelines, cleaner, SafeGuard, standard template.</li>
                                <li><code>company</code> — used in foundation, footer &amp; template names.</li>
                                <li><code>website</code> — company profile context.</li>
                                <li><code>offer</code> — what you sell.</li>
                                <li><code>usps</code> (array) — differentiators; flow into SafeGuard + templates.</li>
                                <li><code>senderName</code> / <code>senderRole</code> / <code>senderEmail</code> — footer/signature + "From" + <code>{`{{user_name}}`}</code>.</li>
                                <li><code>emojis</code> (bool) — whether the sender uses emojis.</li>
                                <li><code>language</code> — <code>de</code>/<code>en</code>/<code>fr</code>/… or <code>auto</code> (default).</li>
                              </ul>
                            </div>

                            <div className="api-setup-group">
                              <span className="api-setup-group-title">audience — who you target</span>
                              <ul className="api-setup-fieldlist">
                                <li><code>description</code> ✅ — SafeGuard target, ICP, email-engine context.</li>
                                <li><code>painPoints</code> (array) — fallback for the template pain point.</li>
                                <li><code>industry</code> — target industry.</li>
                                <li><code>region</code> — <code>DACH</code>/<code>EU</code>/<code>US</code>/<code>UK</code>/<code>global</code>.</li>
                                <li><code>decisionMakerRole</code> — who gets the email at the target.</li>
                                <li><code>excludeIndustries</code> (array) — niches NOT to contact.</li>
                                <li><code>companySize</code> — typical customer size.</li>
                              </ul>
                            </div>

                            <div className="api-setup-group">
                              <span className="api-setup-group-title">brain — the AI voice clone (style only)</span>
                              <ul className="api-setup-fieldlist">
                                <li><code>emails</code> ✅ — <code>{`[{ subject?, body }]`}</code>, ≥2 with a body → clone training samples.</li>
                                <li><code>subjectLines</code> (array) — subject-line samples (count toward the ≥2 minimum).</li>
                                <li><code>tone</code> — <code>casual</code>/<code>formal</code>/<code>mixed</code> (default casual).</li>
                                <li><code>voiceDescription</code> — calibration hint (never used as facts).</li>
                                <li><code>taboos</code> (array) — phrases the clone must never use.</li>
                                <li><code>signOff</code> — <code>{`{ mode: "auto" }`}</code> or <code>{`{ mode: "fixed", fixed: "Best, {{firstName}}" }`}</code>.</li>
                                <li><code>voiceContextEnabled</code> (bool) — use inferred writer background (default true).</li>
                                <li><code>quizAnswers</code> (object) — folded into the clone's voice description.</li>
                              </ul>
                            </div>

                            <div className="api-setup-group">
                              <span className="api-setup-group-title">campaign — the template brief (applies to intro + follow-up)</span>
                              <ul className="api-setup-fieldlist">
                                <li><code>uniqueAdvantage</code> — what makes you stand out.</li>
                                <li><code>painPoint</code> — recipient pain (falls back to <code>audience.painPoints</code>).</li>
                                <li><code>goal</code> — desired outcome (e.g. "book a call").</li>
                                <li><code>references</code> — your own past work / clients.</li>
                                <li><code>fixedFacts</code> (array) — hard facts the AI may cite, never contradicts.</li>
                                <li><code>angles</code> (array) — preferred angles to lean into.</li>
                                <li><code>taboos</code> (array) — campaign-specific claims to avoid.</li>
                                <li><code>extraRules</code> — free-text must-follow rules on top of the brief.</li>
                                <li><code>proof</code> — <code>{`{ caseStudies, testimonials, notableClients, stats }`}</code> (each array) → info pools the AI pulls from.</li>
                                <li><code>voiceAdherence</code> — <code>free</code>/<code>balanced</code>/<code>strict</code>/<code>mirror</code>.</li>
                                <li><code>briefAdherence</code> — <code>loose</code>/<code>balanced</code>/<code>strict</code>.</li>
                                <li><code>language</code> — template output language (falls back to identity).</li>
                              </ul>
                            </div>

                            <div className="api-setup-group">
                              <span className="api-setup-group-title">templates / options</span>
                              <ul className="api-setup-fieldlist">
                                <li><code>templates.introExamples</code> / <code>followUpExamples</code> (array) — well-rated examples the AI may learn from.</li>
                                <li><code>options.forceFullSetup</code> (bool) — re-run foundation even if it exists.</li>
                                <li><code>options.skipLeadResearch</code> (bool) — skip AI lead research (default true).</li>
                                <li><code>options.skipTemplates</code> (bool) — stop after the clone, build templates later.</li>
                                <li><code>options.activateCloneTemplate</code> (bool) — activate created templates (default true).</li>
                                <li><code>external_ref</code> (top level) — your customer id; idempotency (a repeat call returns the same run).</li>
                              </ul>
                            </div>
                          </div>

                          {/* Full example */}
                          <div className="api-setup-doc-block">
                            <div className="api-setup-example-head">
                              <h6>6. Full example body</h6>
                              <button
                                className="api-key-setup-config-copy"
                                onClick={() => copyToClipboard(setupExampleJson)}
                              >
                                {copied ? '✓ Copied' : 'Copy full JSON'}
                              </button>
                            </div>
                            <pre className="api-setup-code">{setupExampleJson}</pre>
                          </div>

                          {/* Minimal example */}
                          <div className="api-setup-doc-block">
                            <div className="api-setup-example-head">
                              <h6>7. Minimal valid body</h6>
                              <button
                                className="api-key-setup-config-copy"
                                onClick={() => copyToClipboard(setupMinimalJson)}
                              >
                                {copied ? '✓ Copied' : 'Copy minimal JSON'}
                              </button>
                            </div>
                            <pre className="api-setup-code">{setupMinimalJson}</pre>
                          </div>

                          {/* Status response */}
                          <div className="api-setup-doc-block">
                            <h6>8. Status response</h6>
                            <pre className="api-setup-code">{`{
  "status": "running | done | failed",
  "phase":  "foundation | clone | templates | done",
  "percent": 0,
  "results": {
    "foundation": { "topicId", "cleanerId", "vaultIds" },
    "clone":      { "cloneId", "status", "trained" },
    "footer":     { "footerId" },
    "templates":  { "introTemplateId", "followUpTemplateId" }
  },
  "errors": [ { "phase", "message", "at" } ]
}`}</pre>
                          </div>

                          {/* Re-run behavior */}
                          <div className="api-setup-doc-block">
                            <h6>9. Running it again on an existing account</h6>
                            <p>Base info is <strong>replaced</strong>; clones &amp; templates are <strong>added on top</strong> — nothing made by hand is ever deleted.</p>
                            <ul className="api-setup-fieldlist">
                              <li><strong>Added (kept additive):</strong> a new AI clone, new templates, a new cleaner (set as default), a new footer. Existing ones stay.</li>
                              <li><strong>Replaced:</strong> AI guidelines, SafeGuard, and the auto-generated Account Vault base notes (about-us, ICP, offer, goals). Vault notes you wrote yourself are kept.</li>
                              <li><strong>Idempotency:</strong> the same <code>external_ref</code> returns the existing run (no second setup). Use a new <code>external_ref</code> to run again on purpose.</li>
                              <li><strong>Reuse:</strong> identical about-us/audience → foundation reused (no extra AI cost); clones &amp; templates are still added. <code>options.forceFullSetup: true</code> forces a full regenerate.</li>
                            </ul>
                          </div>

                        </div>
                      )}
                    </div>
                    )}

                    {detailTab === 'behavior' && (
                    <div className="api-key-mode-section">
                      <h5>Outreach triggers</h5>
                      <p className="api-key-mode-section-hint">
                        Let this key push a single contact into an Outreach Agent that is in API-trigger mode. Off by default — import-only keys stay import-only.
                      </p>
                      <label className="api-key-scope-row">
                        <span>
                          <strong>Allow outreach triggers</strong>
                          <span>POST to <code>outreachApiTrigger</code> with <code>agent</code> + <code>email</code>.</span>
                        </span>
                        <span className="api-setup-switch">
                          <input
                            type="checkbox"
                            checked={key.allowOutreachTrigger === true}
                            onChange={(e) => toggleOutreachTrigger(key.id, e.target.checked)}
                            disabled={saving}
                          />
                          <span className="api-setup-switch-slider" />
                        </span>
                      </label>
                    </div>
                    )}

                    {detailTab === 'behavior' && (
                    <div className="api-key-mode-section">
                      <h5>Webhooks</h5>
                      <p className="api-key-mode-section-hint">
                        SalesGod POSTs <code>import.completed</code>, <code>email.prepared</code>, <code>reply.received</code>, <code>email.sent</code>, <code>email.bounced</code> and <code>lead.status_changed</code> to an https URL.
                        Sent, bounce, status and reply events are for contacts this key imported or triggered.
                        Verify <code>X-SalesGod-Signature</code>: <code>sha256=</code> plus HMAC-SHA256 of <code>{'{timestamp}.{raw body}'}</code> using the secret below.
                      </p>
                      <input
                        type="url"
                        className="ap-input"
                        placeholder="https://example.com/salesgod-hook"
                        value={webhookDrafts[key.id] !== undefined ? webhookDrafts[key.id] : (key.webhookUrl || '')}
                        onChange={(e) => setWebhookDrafts(prev => ({ ...prev, [key.id]: e.target.value }))}
                        disabled={saving}
                      />
                      <div className="api-webhook-actions">
                        <button
                          type="button"
                          className="altBtn altBtn--save"
                          disabled={saving}
                          onClick={() => saveWebhook(key.id, webhookDrafts[key.id] !== undefined ? webhookDrafts[key.id] : (key.webhookUrl || ''))}
                        >
                          Save webhook
                        </button>
                        {key.webhookUrl && (
                          <button
                            type="button"
                            className="altBtn altBtn--petrol"
                            disabled={saving}
                            onClick={() => rotateWebhookSecret(key.id)}
                          >
                            Rotate secret
                          </button>
                        )}
                      </div>
                      {key.webhookSecret && (
                        <p className="api-key-mode-section-hint">
                          Secret <code>{key.webhookSecret}</code>
                          <button type="button" className="api-inline-copy" onClick={() => copyToClipboard(key.webhookSecret)}>
                            {copied ? 'Copied' : 'Copy'}
                          </button>
                        </p>
                      )}
                    </div>
                    )}

                    {/* ───── Import behavior settings — Behavior tab ───── */}
                    {detailTab === 'behavior' && keyPurpose[key.id] === 'import' && (
                    <>
                    {/* Import Mode Toggle */}
                    <div className="api-key-mode-section">
                      <h5>Import Mode</h5>
                      <p className="api-key-mode-section-hint">Choose how imported contacts should be processed.</p>
                      <div className="api-key-mode-toggle three-way">
                        <button 
                          className={`api-key-mode-btn ${key.mode !== 'auto' && key.mode !== 'smart' ? 'active' : ''}`}
                          onClick={() => updateKeyMode(key.id, 'staging', key.autoConfig)}
                          disabled={saving}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span className="mode-btn-label">Review</span>
                        </button>
                        <button 
                          className={`api-key-mode-btn ${key.mode === 'auto' ? 'active auto' : ''}`}
                          onClick={() => updateKeyMode(key.id, 'auto', key.autoConfig)}
                          disabled={saving}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className="mode-btn-label">Direct Import</span>
                        </button>
                        <button 
                          className={`api-key-mode-btn ${key.mode === 'smart' ? 'active smart' : ''}`}
                          onClick={() => updateKeyMode(key.id, 'smart', key.autoConfig)}
                          disabled={saving}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                            <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span className="mode-btn-label">Smart</span>
                        </button>
                      </div>
                      <div className="api-key-mode-info">
                        {key.mode === 'smart' ? (
                          <>
                            <div className="mode-info-title">Smart Pipeline active</div>
                            <p className="mode-info-desc">Contacts are enriched with AI, checked for duplicates, and sorted by rules. Only uncertain cases go to the review queue for manual inspection.</p>
                            <div className="mode-info-features">
                              <span className="mode-info-tag">Dedup 2.0</span>
                              <span className="mode-info-tag">Signal Events</span>
                              <span className="mode-info-tag">Scoring</span>
                              <span className="mode-info-tag">Rule Engine</span>
                            </div>
                          </>
                        ) : key.mode === 'auto' ? (
                          <>
                            <div className="mode-info-title">Direct Import active</div>
                            <p className="mode-info-desc">Contacts are imported directly into your database. Duplicates are automatically detected and handled based on your settings. No manual review needed.</p>
                          </>
                        ) : (
                          <>
                            <div className="mode-info-title">Review Mode active</div>
                            <p className="mode-info-desc">Contacts first land in a staging area. You review and confirm each import manually before the data is added to your database.</p>
                            <span className="mode-info-hint">Recommended for getting started or sensitive data sources</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Field Mapping — visible for auto & smart modes */}
                    {(key.mode === 'auto' || key.mode === 'smart') && (
                      <div className="api-key-field-mapping-section">
                        <h5>Field Mapping</h5>
                        <p className="api-key-mode-section-hint">How should incoming fields be mapped to your database fields?</p>
                        
                        <div className="api-key-mapping-mode-toggle">
                          <button
                            className={`api-key-mapping-mode-btn ${(key.mappingMode || 'auto') === 'auto' ? 'active' : ''}`}
                            onClick={() => updateMappingMode(key.id, 'auto')}
                            disabled={saving}
                          >
                            <span className="mapping-mode-label">Auto-Detect</span>
                            <span className="mapping-mode-desc">Common field names are mapped automatically (email, company, name...)</span>
                          </button>
                          <button
                            className={`api-key-mapping-mode-btn ${key.mappingMode === 'direct' ? 'active' : ''}`}
                            onClick={() => updateMappingMode(key.id, 'direct')}
                            disabled={saving}
                          >
                            <span className="mapping-mode-label">Direct</span>
                            <span className="mapping-mode-desc">Sender uses our field names (lead_email, business_name, ...)</span>
                          </button>
                          <button
                            className={`api-key-mapping-mode-btn ${key.mappingMode === 'custom' ? 'active' : ''}`}
                            onClick={() => updateMappingMode(key.id, 'custom')}
                            disabled={saving}
                          >
                            <span className="mapping-mode-label">Custom</span>
                            <span className="mapping-mode-desc">Define your own field mapping below</span>
                          </button>
                        </div>

                        {key.mappingMode === 'custom' && (
                          <div className="api-key-custom-mapping">
                            <div className="api-key-mapping-header-row">
                              <span className="api-key-mapping-col-label">Incoming Field Name</span>
                              <span className="api-key-mapping-col-label">Your Database Field</span>
                              <span className="api-key-mapping-col-spacer"></span>
                            </div>
                            {(key.fieldMapping || []).map((row, idx) => (
                              <div key={idx} className="api-key-mapping-row">
                                <input
                                  type="text"
                                  className="api-key-mapping-source-input"
                                  placeholder="e.g. Firmenname"
                                  value={row.source || ''}
                                  onChange={(e) => updateFieldMappingRow(key.id, idx, 'source', e.target.value, key.fieldMapping)}
                                />
                                <select
                                  className="api-key-mapping-target-select"
                                  value={row.target || ''}
                                  onChange={(e) => updateFieldMappingRow(key.id, idx, 'target', e.target.value, key.fieldMapping)}
                                >
                                  <option value="">Select...</option>
                                  {availableFields.map(f => (
                                    <option key={f.field} value={f.field}>{f.label} ({f.field})</option>
                                  ))}
                                </select>
                                <button
                                  className="api-key-mapping-remove-btn"
                                  onClick={() => removeFieldMappingRow(key.id, idx, key.fieldMapping)}
                                  title="Remove"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            ))}
                            <div className="api-key-mapping-actions">
                              <button
                                className="api-key-mapping-add-btn"
                                onClick={() => addFieldMappingRow(key.id, key.fieldMapping)}
                              >
                                + Add Field
                              </button>
                              {(key.fieldMapping || []).some(m => m.source?.trim() && m.target?.trim()) && (
                                <button
                                  className="api-key-mapping-save-btn"
                                  onClick={() => saveFieldMapping(key.id, key.fieldMapping)}
                                  disabled={saving}
                                >
                                  {saving ? 'Saving...' : 'Save Mapping'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Auto Config — only visible in auto mode */}
                    {key.mode === 'auto' && (
                      <div className="api-key-auto-config">
                        <div className="api-key-auto-config-group">
                          <label className="api-key-auto-config-label">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <path d="M16 8V6C16 4.89543 15.1046 4 14 4H6C4.89543 4 4 4.89543 4 6V14C4 15.1046 4.89543 16 6 16H8" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Duplikat-Erkennung
                          </label>
                          <p className="api-key-auto-config-hint">Kontakte gelten als Duplikat, wenn eines dieser Felder übereinstimmt.</p>
                          <div className="api-key-dedup-fields">
                            {DEDUP_FIELD_OPTIONS.map(opt => {
                              const isChecked = (key.autoConfig?.dedupeFields || ['lead_email']).includes(opt.key);
                              return (
                                <label key={opt.key} className={`api-key-dedup-field ${isChecked ? 'checked' : ''} ${opt.fixed ? 'fixed' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    disabled={opt.fixed || saving}
                                    onChange={() => !opt.fixed && toggleDedupeField(key.id, opt.key, key.autoConfig)}
                                  />
                                  <span>{opt.label}</span>
                                  {opt.fixed && <span className="api-key-dedup-fixed">Pflichtfeld</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="api-key-auto-config-group">
                          <label className="api-key-auto-config-label">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Duplikat-Verhalten
                          </label>
                          <div className="api-key-dup-actions">
                            <button
                              className={`api-key-dup-action-btn ${(key.autoConfig?.duplicateAction || 'merge') === 'merge' ? 'active' : ''}`}
                              onClick={() => setDuplicateAction(key.id, 'merge', key.autoConfig)}
                              disabled={saving}
                            >
                              <span className="dup-action-icon">🔀</span>
                              <span className="dup-action-label">Zusammenführen</span>
                              <span className="dup-action-desc">Fehlende Felder ergänzen</span>
                            </button>
                            <button
                              className={`api-key-dup-action-btn ${key.autoConfig?.duplicateAction === 'skip' ? 'active' : ''}`}
                              onClick={() => setDuplicateAction(key.id, 'skip', key.autoConfig)}
                              disabled={saving}
                            >
                              <span className="dup-action-icon">⏭️</span>
                              <span className="dup-action-label">Überspringen</span>
                              <span className="dup-action-desc">Bestehende Daten behalten</span>
                            </button>
                            <button
                              className={`api-key-dup-action-btn ${key.autoConfig?.duplicateAction === 'update' ? 'active' : ''}`}
                              onClick={() => setDuplicateAction(key.id, 'update', key.autoConfig)}
                              disabled={saving}
                            >
                              <span className="dup-action-icon">♻️</span>
                              <span className="dup-action-label">Überschreiben</span>
                              <span className="dup-action-desc">Komplett ersetzen</span>
                            </button>
                          </div>
                        </div>

                        {/* Auto Import Logs */}
                        <div className="api-key-auto-logs">
                          <div className="api-key-auto-logs-header">
                            <label className="api-key-auto-config-label">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Import-Verlauf
                            </label>
                            <button 
                              className="api-key-logs-refresh"
                              onClick={() => loadAutoLogs(key.id)} 
                              disabled={loadingLogs[key.id]}
                            >
                              {loadingLogs[key.id] ? 'Laden...' : 'Aktualisieren'}
                            </button>
                          </div>
                          {loadingLogs[key.id] ? (
                            <div className="api-key-logs-loading">
                              <div className="api-settings-spinner" style={{width: 20, height: 20}}></div>
                            </div>
                          ) : !autoLogs[key.id] || autoLogs[key.id].length === 0 ? (
                            <p className="api-key-no-logs">Noch keine Imports aufgezeichnet.</p>
                          ) : (
                            <div className="api-key-logs-list">
                              {autoLogs[key.id].map(log => (
                                <div key={log.id} className="api-key-log-item">
                                  <div className="api-key-log-header-row">
                                    <span className="api-key-log-date">{formatDate(log.createdAt)}</span>
                                    <span className="api-key-log-name">{log.import_name}</span>
                                    <span className="api-key-log-duration">{log.duration_ms}ms</span>
                                  </div>
                                  <div className="api-key-log-stats">
                                    {log.inserted > 0 && <span className="log-stat log-stat-inserted">{log.inserted} inserted</span>}
                                    {log.merged > 0 && <span className="log-stat log-stat-merged">{log.merged} merged</span>}
                                    {log.updated > 0 && <span className="log-stat log-stat-updated">{log.updated} updated</span>}
                                    {log.skipped_empty > 0 && <span className="log-stat log-stat-skipped">{log.skipped_empty} empty</span>}
                                    {log.skipped_duplicate > 0 && <span className="log-stat log-stat-skipped">{log.skipped_duplicate} skipped</span>}
                                    {log.dedupe_conflicts > 0 && <span className="log-stat log-stat-conflict">{log.dedupe_conflicts} conflicts</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Smart Config — only visible in smart mode */}
                    {key.mode === 'smart' && (() => {
                      const sc = key.smartConfig || getDefaultSmartConfig();
                      return (
                        <div className="api-key-smart-config">
                          <div className="smart-config-header">
                            <h5>Smart Import Pipeline</h5>
                            <p className="smart-config-subtitle">Deine Kontakte durchlaufen automatisch alle aktiven Schritte. Aktiviere nur, was du brauchst.</p>
                          </div>

                          {reviewQueueCount > 0 && (
                            <div className="smart-review-banner">
                              <div className="smart-review-banner-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </div>
                              <div className="smart-review-banner-text">
                                <strong>{reviewQueueCount}</strong> {reviewQueueCount !== 1 ? 'Kontakte warten' : 'Kontakt wartet'} auf deine Prüfung
                              </div>
                              <button
                                className="smart-review-banner-btn"
                                onClick={() => setShowSmartReview(true)}
                              >
                                Jetzt prüfen
                              </button>
                            </div>
                          )}

                          {/* Step 1 — Enrichment */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">1</span>
                              <div>
                                <h6 className="smart-section-title">Anreichern & Analysieren</h6>
                                <p className="smart-section-hint">Kontakte werden beim Import automatisch mit zusätzlichen Daten angereichert.</p>
                              </div>
                            </div>
                            <div className="smart-pipeline-grid">
                              <div className={`smart-pipeline-card ${sc.enableSignalEvents !== false ? 'enabled' : ''}`}>
                                <div className="smart-pipeline-card-header">
                                  <div className={`smart-pipeline-indicator ${sc.enableSignalEvents !== false ? 'on' : 'off'}`} />
                                  <h6>Signal Events</h6>
                                </div>
                                <p>Erkennt automatisch Hiring-, Wachstums- und Kaufsignale aus importierten Daten.</p>
                                <span className="smart-pipeline-benefit">Erreiche Leads zum perfekten Zeitpunkt</span>
                                <button
                                  className={`smart-toggle-btn ${sc.enableSignalEvents !== false ? 'active' : ''}`}
                                  onClick={() => toggleSmartConfigBool(key.id, 'enableSignalEvents', sc)}
                                  disabled={saving}
                                >
                                  {sc.enableSignalEvents !== false ? 'Aktiv' : 'Deaktiviert'}
                                </button>
                              </div>

                              <div className={`smart-pipeline-card ${sc.enableScoring !== false ? 'enabled' : ''}`}>
                                <div className="smart-pipeline-card-header">
                                  <div className={`smart-pipeline-indicator ${sc.enableScoring !== false ? 'on' : 'off'}`} />
                                  <h6>Company Scoring</h6>
                                </div>
                                <p>Berechnet automatisch Momentum- und Intent-Scores pro Firma.</p>
                                <span className="smart-pipeline-benefit">Priorisiere die vielversprechendsten Firmen</span>
                                <button
                                  className={`smart-toggle-btn ${sc.enableScoring !== false ? 'active' : ''}`}
                                  onClick={() => toggleSmartConfigBool(key.id, 'enableScoring', sc)}
                                  disabled={saving}
                                >
                                  {sc.enableScoring !== false ? 'Aktiv' : 'Deaktiviert'}
                                </button>
                              </div>

                              <div className={`smart-pipeline-card ${sc.enableCleaner ? 'enabled' : ''}`}>
                                <div className="smart-pipeline-card-header">
                                  <div className={`smart-pipeline-indicator ${sc.enableCleaner ? 'on' : 'off'}`} />
                                  <h6>AI Cleaner</h6>
                                </div>
                                <p>Bereinigt Kontaktdaten automatisch nach deinen Richtlinien.</p>
                                <span className="smart-pipeline-benefit">Saubere Daten ohne manuellen Aufwand</span>
                                <button
                                  className={`smart-toggle-btn ${sc.enableCleaner ? 'active' : ''}`}
                                  onClick={() => toggleSmartConfigBool(key.id, 'enableCleaner', sc)}
                                  disabled={saving}
                                >
                                  {sc.enableCleaner ? 'Aktiv' : 'Deaktiviert'}
                                </button>
                              </div>

                              <div className={`smart-pipeline-card ${sc.enableSpy ? 'enabled' : ''}`}>
                                <div className="smart-pipeline-card-header">
                                  <div className={`smart-pipeline-indicator ${sc.enableSpy ? 'on' : 'off'}`} />
                                  <h6>SPY Enrichment</h6>
                                </div>
                                <p>Findet automatisch Social-Profile, E-Mail-Adressen und Entscheider.</p>
                                <span className="smart-pipeline-benefit">Vollständige Profile für bessere Outreach</span>
                                <button
                                  className={`smart-toggle-btn ${sc.enableSpy ? 'active' : ''}`}
                                  onClick={() => toggleSmartConfigBool(key.id, 'enableSpy', sc)}
                                  disabled={saving}
                                >
                                  {sc.enableSpy ? 'Aktiv' : 'Deaktiviert'}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Step 2 — Dedup */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">2</span>
                              <div>
                                <h6 className="smart-section-title">Duplikate erkennen</h6>
                                <p className="smart-section-hint">Verhindert, dass derselbe Kontakt mehrfach in deiner Datenbank landet. Wähle, welche Felder zum Abgleich genutzt werden.</p>
                              </div>
                            </div>
                            <div className="smart-dedup-options">
                              <label className={`smart-dedup-option ${sc.stripPlusAddressing ? 'checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={sc.stripPlusAddressing !== false}
                                  onChange={() => toggleSmartConfigBool(key.id, 'stripPlusAddressing', sc)}
                                  disabled={saving}
                                />
                                <div>
                                  <span className="smart-dedup-option-label">Plus-Adressen ignorieren</span>
                                  <span className="smart-dedup-option-desc">john+test@firma.de wird als john@firma.de erkannt</span>
                                </div>
                              </label>
                              <label className={`smart-dedup-option ${sc.enableDomainDedup ? 'checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={sc.enableDomainDedup !== false}
                                  onChange={() => toggleSmartConfigBool(key.id, 'enableDomainDedup', sc)}
                                  disabled={saving}
                                />
                                <div>
                                  <span className="smart-dedup-option-label">Domain-Matching</span>
                                  <span className="smart-dedup-option-desc">Erkennt Kontakte derselben Firma anhand der Domain</span>
                                </div>
                              </label>
                            </div>
                            <div className="smart-identity-keys-wrapper">
                              <span className="smart-identity-keys-label">Abgleich über:</span>
                              <div className="smart-identity-keys">
                                <span className="smart-identity-key active">Email</span>
                                <span className="smart-identity-key active">Domain</span>
                                <span className="smart-identity-key active">LinkedIn URL</span>
                                <span className="smart-identity-key active">Job URL</span>
                              </div>
                            </div>
                          </div>

                          {/* Step 3 — Duplicate handling */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">3</span>
                              <div>
                                <h6 className="smart-section-title">Duplikat-Verhalten</h6>
                                <p className="smart-section-hint">Was soll passieren, wenn ein Kontakt bereits existiert?</p>
                              </div>
                            </div>
                            <div className="api-key-dup-actions">
                              <button
                                className={`api-key-dup-action-btn ${(sc.duplicateAction || 'merge') === 'merge' ? 'active' : ''}`}
                                onClick={() => updateSmartConfig(key.id, { ...sc, duplicateAction: 'merge' })}
                                disabled={saving}
                                title="Neue Daten werden mit vorhandenen zusammengeführt"
                              >
                                <span className="dup-action-icon">🔀</span>
                                <span className="dup-action-label">Zusammenführen</span>
                                <span className="dup-action-desc">Fehlende Felder ergänzen</span>
                              </button>
                              <button
                                className={`api-key-dup-action-btn ${sc.duplicateAction === 'skip' ? 'active' : ''}`}
                                onClick={() => updateSmartConfig(key.id, { ...sc, duplicateAction: 'skip' })}
                                disabled={saving}
                                title="Duplikat wird ignoriert, bestehender Kontakt bleibt unverändert"
                              >
                                <span className="dup-action-icon">⏭️</span>
                                <span className="dup-action-label">Überspringen</span>
                                <span className="dup-action-desc">Bestehende Daten behalten</span>
                              </button>
                              <button
                                className={`api-key-dup-action-btn ${sc.duplicateAction === 'update' ? 'active' : ''}`}
                                onClick={() => updateSmartConfig(key.id, { ...sc, duplicateAction: 'update' })}
                                disabled={saving}
                                title="Alle Felder werden mit den neuen Daten überschrieben"
                              >
                                <span className="dup-action-icon">♻️</span>
                                <span className="dup-action-label">Überschreiben</span>
                                <span className="dup-action-desc">Komplett ersetzen</span>
                              </button>
                            </div>
                          </div>

                          {/* Step 4 — Quality Gate */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">4</span>
                              <div>
                                <h6 className="smart-section-title">Qualitätskontrolle</h6>
                                <p className="smart-section-hint">Kontakte, die unter dem Schwellenwert liegen, landen in deiner Review-Queue zur manuellen Prüfung.</p>
                              </div>
                            </div>
                            <div className="smart-threshold-wrapper">
                              <div className="smart-threshold-value">{Math.round((sc.reviewThreshold || 0.60) * 100)}%</div>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={Math.round((sc.reviewThreshold || 0.60) * 100)}
                                onChange={(e) => updateSmartConfig(key.id, {
                                  ...sc,
                                  reviewThreshold: parseInt(e.target.value) / 100
                                })}
                                className="smart-threshold-slider"
                                disabled={saving}
                              />
                              <div className="smart-threshold-labels">
                                <span>Alles importieren</span>
                                <span>Mehr prüfen</span>
                              </div>
                            </div>
                          </div>

                          {/* Step 5 — Rules */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">5</span>
                              <div>
                                <h6 className="smart-section-title">Import-Regeln</h6>
                                <p className="smart-section-hint">Automatisiere wiederkehrende Aufgaben beim Import. Regeln werden bei jedem neuen Kontakt geprüft und sofort angewendet.</p>
                              </div>
                            </div>
                            <div className="smart-rules-examples">
                              <div className="smart-rules-example">
                                <span className="smart-rules-example-if">Wenn</span> Jobtitel enthält "Sales" oder "SDR"
                                <span className="smart-rules-example-then">→</span> Tag "Sales-Team" setzen + Priorität erhöhen
                              </div>
                              <div className="smart-rules-example">
                                <span className="smart-rules-example-if">Wenn</span> Firmengröße {">"} 50 Mitarbeiter
                                <span className="smart-rules-example-then">→</span> Route auf "Enterprise" setzen
                              </div>
                              <div className="smart-rules-example">
                                <span className="smart-rules-example-if">Wenn</span> keine E-Mail und keine Website
                                <span className="smart-rules-example-then">→</span> Kontakt überspringen
                              </div>
                            </div>
                            <button
                              className="smart-rules-btn"
                              onClick={() => setShowRuleEditor(true)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                              Regeln verwalten
                            </button>
                          </div>

                          {/* Step 6 — Import Logs */}
                          <div className="smart-section">
                            <div className="smart-section-header">
                              <span className="smart-section-step">6</span>
                              <div>
                                <h6 className="smart-section-title">Import-Verlauf</h6>
                                <p className="smart-section-hint">Hier siehst du alle bisherigen Smart-Imports mit Details zu Duplikaten, Regeln und Signalen.</p>
                              </div>
                            </div>
                            <div className="api-key-auto-logs">
                              <div className="api-key-auto-logs-header">
                                <label className="api-key-auto-config-label">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  Letzte Imports
                                </label>
                                <button 
                                  className="api-key-logs-refresh"
                                  onClick={() => loadAutoLogs(key.id)} 
                                  disabled={loadingLogs[key.id]}
                                >
                                  {loadingLogs[key.id] ? 'Laden...' : 'Aktualisieren'}
                                </button>
                              </div>
                              {loadingLogs[key.id] ? (
                                <div className="api-key-logs-loading">
                                  <div className="api-settings-spinner" style={{width: 20, height: 20}}></div>
                                </div>
                              ) : !autoLogs[key.id] || autoLogs[key.id].length === 0 ? (
                                <p className="api-key-no-logs">Noch keine Smart-Imports aufgezeichnet.</p>
                              ) : (
                                <div className="api-key-logs-list">
                                  {autoLogs[key.id].map(log => (
                                    <div key={log.id} className="api-key-log-item">
                                      <div className="api-key-log-header-row">
                                        <span className="api-key-log-date">{formatDate(log.createdAt)}</span>
                                        <span className="api-key-log-name">{log.import_name}</span>
                                        <span className="api-key-log-duration">{log.duration_ms}ms</span>
                                      </div>
                                      <div className="api-key-log-stats">
                                        {log.inserted > 0 && <span className="log-stat log-stat-inserted">{log.inserted} neu</span>}
                                        {log.merged > 0 && <span className="log-stat log-stat-merged">{log.merged} zusammengeführt</span>}
                                        {log.review_queued > 0 && <span className="log-stat log-stat-review">{log.review_queued} zur Prüfung</span>}
                                        {log.rules_matched > 0 && <span className="log-stat log-stat-rules">{log.rules_matched} Regeln</span>}
                                        {log.companies_resolved > 0 && <span className="log-stat log-stat-companies">{log.companies_resolved} Firmen erkannt</span>}
                                        {log.signals_created > 0 && <span className="log-stat log-stat-signals">{log.signals_created} Signale</span>}
                                        {log.skipped_rule > 0 && <span className="log-stat log-stat-skipped">{log.skipped_rule} per Regel übersprungen</span>}
                                        {log.skipped_empty > 0 && <span className="log-stat log-stat-skipped">{log.skipped_empty} leer</span>}
                                      </div>
                                      {log.pipeline && (
                                        <div className="api-key-log-pipeline">
                                          {Object.entries(log.pipeline).map(([k, v]) => (
                                            <span key={k} className={`pipeline-badge ${v === 'disabled' ? 'off' : ''}`}>
                                              {k}: {v}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    </>
                    )}

                    {detailTab === 'activity' && (
                      <div className="api-key-detail-stats">
                        <div className="api-key-detail-stat">
                          <span className="stat-value">{formatNumber(key.mode === 'auto' || key.mode === 'smart' ? key.totalImported : key.totalStaged)}</span>
                          <span className="stat-label">{key.mode === 'auto' || key.mode === 'smart' ? 'Imported' : 'Staged'}</span>
                        </div>
                        <div className="api-key-detail-stat">
                          <span className="stat-value">{formatNumber(key.requestCount)}</span>
                          <span className="stat-label">Requests</span>
                        </div>
                        <div className="api-key-detail-stat">
                          <span className="stat-value">{key.lastUsed ? formatDate(key.lastUsed).split(',')[0] : 'Never'}</span>
                          <span className="stat-label">Last Used</span>
                        </div>
                        <div className="api-key-detail-stat">
                          <span className="stat-value">{formatDate(key.createdAt).split(',')[0]}</span>
                          <span className="stat-label">Created</span>
                        </div>
                      </div>
                    )}

                    {detailTab === 'behavior' && keyPurpose[key.id] === 'setup' && (
                      <div className="ap-info-panel">
                        <div>
                          <h5>This key does not import contacts</h5>
                          <p>It only lets another app set up the account. Open <button type="button" className="ap-inline-link" onClick={() => setDetailTab('connect')}>Connect</button> for the URL and an example request.</p>
                        </div>
                      </div>
                    )}

                    {detailTab === 'connect' && !keyPurpose[key.id] && (
                      <div className="ap-info-panel">
                        <div>
                          <h5>Choose what this key is for first</h5>
                          <p>Open <button type="button" className="ap-inline-link" onClick={() => setDetailTab('behavior')}>Settings</button> and pick import or account setup.</p>
                        </div>
                      </div>
                    )}

                    {detailTab === 'activity' && (
                      <div className="ap-info-panel activity">
                        <div>
                          <h5>{key.lastUsed ? 'Recent requests' : 'No requests yet'}</h5>
                          <p>
                            {key.lastUsed
                              ? <>Last request {formatDate(key.lastUsed)}. {formatNumber(key.requestCount)} calls · {formatNumber((key.mode === 'auto' || key.mode === 'smart') ? (key.totalImported || 0) : key.totalStaged)} contacts.</>
                              : <>As soon as another app uses this key, the counts show up here.</>}
                          </p>
                          {(key.mode === 'auto' || key.mode === 'smart') && key.mode === 'smart' && (
                            <button
                              type="button"
                              className="altBtn altBtn--petrol"
                              onClick={() => setShowSmartReview(true)}
                              style={{ marginTop: 12 }}
                            >
                              Open Smart review queue
                            </button>
                          )}
                          {key.mode !== 'auto' && key.mode !== 'smart' && (
                            <button
                              type="button"
                              className="altBtn altBtn--petrol"
                              onClick={() => setShowStagingReview(true)}
                              style={{ marginTop: 12 }}
                              disabled={!pendingImports.contacts}
                            >
                              {pendingImports.contacts ? `Review ${formatNumber(pendingImports.contacts)} staged contacts` : 'Nothing to review'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
              </div>
            ))}
        </main>
      </div>

      {/* Reference footer — How it works + Docs as flat tabs */}
      {apiKeys.length > 0 && (
        <div className="ap-reference">
          <nav className="ap-reference-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={`ap-reference-tab ${footerTab === 'how' ? 'active' : ''}`}
              aria-selected={footerTab === 'how'}
              onClick={() => setFooterTab(footerTab === 'how' ? null : 'how')}
            >
              <i className="fa fa-compass" />
              How it works
            </button>
            <button
              type="button"
              role="tab"
              className={`ap-reference-tab ${footerTab === 'docs' ? 'active' : ''}`}
              aria-selected={footerTab === 'docs'}
              onClick={() => setFooterTab(footerTab === 'docs' ? null : 'docs')}
            >
              <i className="fa fa-book" />
              Documentation
            </button>
          </nav>

          {footerTab === 'how' && (
            <div className="ap-reference-panel">
              <div className="api-workflow">
                <div className="api-workflow-step">
                  <div className="api-workflow-number">1</div>
                  <div className="api-workflow-content">
                    <h4>Send contacts via API</h4>
                    <p>Use your API key to send contacts from external tools (Zapier, Make, scripts, etc.)</p>
                  </div>
                  <div className="api-workflow-connector">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="api-workflow-step">
                  <div className="api-workflow-number">2</div>
                  <div className="api-workflow-content">
                    <h4>Contacts staged for review</h4>
                    <p>Contacts wait in a staging area - you get a notification when new data arrives</p>
                  </div>
                  <div className="api-workflow-connector">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="api-workflow-step">
                  <div className="api-workflow-number">3</div>
                  <div className="api-workflow-content">
                    <h4>Review & import</h4>
                    <p>Preview the data, then import to your database with one click</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {footerTab === 'docs' && (
            <div className="ap-reference-panel">
              <div className="api-spec-row">
                <div>
                  <strong>OpenAPI spec</strong>
                  <p>Paste this URL into ChatGPT Actions, Claude, or Make. Auth header is <code>x-api-key</code>.</p>
                  <code>{apiBaseUrl}/worldDbApiSpec</code>
                </div>
                <button type="button" className="altBtn altBtn--petrol" onClick={() => copyToClipboard(`${apiBaseUrl}/worldDbApiSpec`)}>
                  {copied ? 'Copied' : 'Copy spec URL'}
                </button>
              </div>
              <div className="api-spec-row">
                <div>
                  <strong>MCP server</strong>
                  <p>Connect Claude or ChatGPT to this URL. Same <code>x-api-key</code>, or <code>Authorization: Bearer</code>. Tools read a contact, save a reply draft, and check a prepared email. Nothing is sent.</p>
                  <code>{apiBaseUrl}/salesGodMcp</code>
                </div>
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={() => {
                    navigator.clipboard.writeText(`${apiBaseUrl}/salesGodMcp`);
                    setCopiedMcp(true);
                    setTimeout(() => setCopiedMcp(false), 2000);
                  }}
                >
                  {copiedMcp ? 'Copied' : 'Copy MCP URL'}
                </button>
              </div>
              <div className="api-docs-tabs">
            <button 
              className={`api-docs-tab ${activeDocTab === 'quickstart' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('quickstart')}
            >
              Quick Start
            </button>
            <button 
              className={`api-docs-tab ${activeDocTab === 'fields' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('fields')}
            >
              Available Fields
            </button>
            <button 
              className={`api-docs-tab ${activeDocTab === 'examples' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('examples')}
            >
              Code Examples
            </button>
            <button 
              className={`api-docs-tab ${activeDocTab === 'response' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('response')}
            >
              Response Format
            </button>
          </div>

          <div className="api-docs-content">
            {activeDocTab === 'quickstart' && (
              <div className="api-docs-quickstart">
                <div className="api-endpoint-info">
                  <h4>API Endpoint</h4>
                  <div className="api-endpoint">
                    <span className="api-method">POST</span>
                    <code>{apiBaseUrl}/worldDbApiImport</code>
                    <button 
                      className="api-copy-btn"
                      onClick={() => copyToClipboard(`${apiBaseUrl}/worldDbApiImport`)}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="api-headers-info">
                  <h4>Required Headers</h4>
                  <table className="api-headers-table">
                    <thead>
                      <tr>
                        <th>Header</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>Content-Type</code></td>
                        <td><code>application/json</code></td>
                      </tr>
                      <tr>
                        <td><code>x-api-key</code></td>
                        <td><code>sk_••••••••••••••••••••</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="api-body-info">
                  <h4>Request Body</h4>
                  <p>Send a single contact or multiple contacts:</p>
                  
                  <div className="api-code-block">
                    <div className="api-code-header">
                      <span>Single Contact</span>
                    </div>
                    <pre>{`{
  "lead_email": "john@example.com",
  "dm_name": "John Doe",
  "business_name": "Example Corp"
}`}</pre>
                  </div>

                  <div className="api-code-block">
                    <div className="api-code-header">
                      <span>Multiple Contacts</span>
                    </div>
                    <pre>{`{
  "contacts": [
    { "lead_email": "john@example.com", ... },
    { "lead_email": "jane@example.com", ... }
  ]
}`}</pre>
                  </div>

                  <div className="api-code-block">
                    <div className="api-code-header">
                      <span>With Custom Import Name (optional)</span>
                    </div>
                    <pre>{`{
  "contacts": [...],
  "import_name": "Facebook Leads March 2026"
}`}</pre>
                  </div>
                </div>

                <div className="api-import-name-info">
                  <h4>Staging System</h4>
                  <p>
                    Contacts are <strong>not imported directly</strong> - they wait in a staging area for your review.
                    This gives you full control over what enters your database. Use <code>"import_name"</code> to organize 
                    different sources (e.g., "Facebook Leads", "Website Signups").
                  </p>
                </div>

                <div className="api-limits-info">
                  <h4>Limits & Options</h4>
                  <ul>
                    <li>Maximum <strong>5,000 contacts</strong> per request</li>
                    <li>Duplicate emails are automatically skipped</li>
                    <li>All fields are optional - you can map them later during import</li>
                    <li><code>import_name</code> is optional - defaults to your API key name</li>
                  </ul>
                </div>
              </div>
            )}

            {activeDocTab === 'fields' && (
              <div className="api-docs-fields">
                <p className="api-fields-intro">
                  All fields are <strong>optional</strong>. Send only what you have — missing fields are simply skipped.
                  The API auto-recognises common column names (the <em>Aliases</em> column) so you usually don't need
                  to rename anything on your side. Any unknown field is preserved as a <code>custom_*</code> field
                  that you can map to your custom fields later in the staging review.
                </p>

                {FIELD_GROUP_ORDER
                  .filter(g => (groupedFields[g] || []).length > 0)
                  .map(groupName => (
                    <div key={groupName} className="api-fields-group">
                      <h5 className="api-fields-group-title">{groupName}</h5>
                      <table className="api-fields-table">
                        <thead>
                          <tr>
                            <th style={{ width: '22%' }}>Field</th>
                            <th style={{ width: '20%' }}>Label</th>
                            <th>Description</th>
                            <th style={{ width: '32%' }}>Accepted aliases</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedFields[groupName].map(field => (
                            <tr key={field.field}>
                              <td><code>{field.field}</code></td>
                              <td>{field.label}</td>
                              <td>{field.description}</td>
                              <td>
                                {field.aliases && field.aliases.length > 0
                                  ? field.aliases.map((alias, idx) => (
                                      <span key={alias}>
                                        <code className="api-fields-alias">{alias}</code>
                                        {idx < field.aliases.length - 1 ? ' ' : ''}
                                      </span>
                                    ))
                                  : <span className="api-fields-no-alias">—</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                }
              </div>
            )}

            {activeDocTab === 'examples' && (
              <div className="api-docs-examples">
                <div className="api-example-tabs">
                  {['curl', 'javascript', 'python', 'zapier'].map(lang => (
                    <button
                      key={lang}
                      className={`api-example-tab ${activeDocTab === 'examples' ? 'active' : ''}`}
                      onClick={(e) => {
                        document.querySelectorAll('.api-example-content').forEach(el => el.style.display = 'none');
                        document.querySelector(`#example-${lang}`).style.display = 'block';
                        document.querySelectorAll('.api-example-tab').forEach(el => el.classList.remove('selected'));
                        e.target.classList.add('selected');
                      }}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>
                
                {Object.entries(codeExamples).map(([lang, code], index) => (
                  <div 
                    key={lang}
                    id={`example-${lang}`}
                    className="api-example-content"
                    style={{ display: index === 0 ? 'block' : 'none' }}
                  >
                    <div className="api-code-block">
                      <div className="api-code-header">
                        <span>{lang.charAt(0).toUpperCase() + lang.slice(1)}</span>
                        <button 
                          className="api-copy-btn"
                          onClick={() => copyToClipboard(code)}
                        >
                          Copy
                        </button>
                      </div>
                      <pre>{code}</pre>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeDocTab === 'response' && (
              <div className="api-docs-response">
                <h4>Success Response (200)</h4>
                <div className="api-code-block">
                  <pre>{`{
  "success": true,
  "message": "5 contacts staged for review",
  "batchId": "batch_1706889600000_abc123",
  "importName": "Your API Key Name",
  "staged": 5,
  "note": "Contacts are NOT imported yet. Log into SalesGod to review and import them."
}`}</pre>
                </div>

                <div className="api-staging-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Contacts are staged for review - they are NOT automatically imported into your database.</span>
                </div>

                <h4>Error Responses</h4>
                <table className="api-response-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>401</code></td>
                      <td>Missing or invalid API key</td>
                    </tr>
                    <tr>
                      <td><code>403</code></td>
                      <td>API key is deactivated</td>
                    </tr>
                    <tr>
                      <td><code>400</code></td>
                      <td>Invalid request body or too many contacts</td>
                    </tr>
                    <tr>
                      <td><code>405</code></td>
                      <td>Method not allowed (use POST)</td>
                    </tr>
                    <tr>
                      <td><code>500</code></td>
                      <td>Internal server error</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Staging Review Modal */}
      {showStagingReview && (
        <ApiStagingReview 
          onClose={() => setShowStagingReview(false)}
          onImportComplete={(result) => {
            loadApiKeys();
          }}
        />
      )}

      {/* Smart Review Queue Modal */}
      {showSmartReview && (
        <SmartReviewQueue
          onClose={() => {
            setShowSmartReview(false);
            loadApiKeys();
          }}
        />
      )}

      {/* Import Rule Editor Modal */}
      {showRuleEditor && (
        <ImportRuleEditor
          onClose={() => setShowRuleEditor(false)}
        />
      )}
    </div>
  );
};

export default ApiSettings;
